import { useEffect, useLayoutEffect, useMemo, useRef, useState, Fragment } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { Sun, Moon, Plus, Pencil, Trash2, AlertTriangle, FileSpreadsheet, Filter, X, ArrowUp, ArrowDown, ArrowUpDown, ArrowLeftRight, ArrowRight, Table2 } from "lucide-react";
import {
  getColaboradoresMaoDeObra, postColaboradorMaoDeObra, getTabelaSalarialMaoDeObra,
  putColaboradorMaoDeObra, putReajusteMaoDeObra, putSemReajusteMaoDeObra, deleteColaboradorMaoDeObra,
  getEquipamentosTicketMedio, getAssinaturasTicketMedio, getKitAdmissaoColaborador, getCargosMaoDeObra,
} from "../../api";
import PerfilAssinaturasKit, { EquipamentosKit, custoEquipamentosKit, estimativaAssinaturasKit } from "./PerfilAssinaturasKit";
import SideBarNavGestaoOrcamentaria from "../../componets/SideBarNavGestaoOrcamentaria";
import { KpiRowOrcamento, GaugeProgresso, TrendAccessory } from "../dashboard/KpiCard";
import {
  NIVEIS_CARGO, MODELOS_TRABALHO, REGIMES_MAO_DE_OBRA, AGRUPAMENTOS, ENQUADRAMENTOS,
  MESES_MAO_DE_OBRA, VISOES_MAO_DE_OBRA,
} from "../../constants/maoDeObra";
import { EQUIPAMENTOS_KIT_ADMISSAO_PADRAO, NOME_PROJETO_RATEIO_OPERACOES } from "../../constants/custosGeraisProjeto";
import { DEPARTAMENTO_OPERACAO } from "../../constants/departamentosOrcamento";
import { MESES_ABREV, formatCurrency, formatPercent } from "../../utils/orcamento";

const palette = (isLight) => ({
  bg: isLight ? "#F8F7FC" : "#0F0A1E",
  card: isLight ? "#FFFFFF" : "#1A1030",
  border: isLight ? "#E5E0F0" : "#2E2050",
  text: isLight ? "#1A1030" : "#EDE9FA",
  muted: isLight ? "#7C6FA0" : "#9585C0",
  brand: "#7C3AED",
  success: "#10B981",
  danger: "#EF4444",
  warning: "#D97706",
  navy: "#0F1B33",
  navyText: "#FFFFFF",
  lavanda: "#FBF3FC",
  rowAlt: isLight ? "#FAF9FD" : "#170F28",
});

const ANO_ATUAL = new Date().getFullYear();
const OPCOES_ANO = Array.from({ length: ANO_ATUAL + 2 - 2025 + 1 }, (_, i) => 2025 + i);

// Mesma chave do backend (normalizar_cargo): sem acento, caixa e espaços extras.
function chaveCargo(cargo) {
  return (cargo || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().split(/\s+/).filter(Boolean).join(" ");
}

// Reajustes ativos do ano que mudam o cargo (promoção) — exibidos sob o cargo na tabela.
function promocoesNoAno(colaborador) {
  let cargoAnterior = colaborador.cargo;
  const promocoes = [];
  for (const ordem of [1, 2]) {
    const r = reajusteAtual(colaborador, ordem);
    if (r.ativo && r.mes && r.cargo && chaveCargo(r.cargo) !== chaveCargo(cargoAnterior)) {
      promocoes.push({ ordem, cargo: r.cargo, mes: r.mes });
      cargoAnterior = r.cargo;
    }
  }
  return promocoes;
}

function reajusteAtual(colaborador, ordem) {
  return colaborador[`reajuste_${ordem}`] || { ativo: false, cargo: null, agrupamento: null, enquadramento: null, regime: null, salario: null, salario_excecao: false, mes: null };
}

// Status Reajuste: calculado só no front, a partir do Reajuste 1 e/ou 2 já cadastrados —
// "Inserido" quando pelo menos um dos dois está marcado como ativo, ou quando o colaborador foi
// marcado como "sem reajuste no ano" (conta como preenchido no Progresso Orçamentário).
function reajustePreenchido(colaborador) {
  return !!(colaborador.sem_reajuste || reajusteAtual(colaborador, 1).ativo || reajusteAtual(colaborador, 2).ativo);
}

// Trocas de regime nos reajustes ativos (ex.: Estágio → CLT no Reajuste 1). O regime "de" é o
// Regime Atual para o Reajuste 1 e o regime em vigor depois do Reajuste 1 para o Reajuste 2 —
// reajuste sem regime informado mantém o anterior.
function trocasDeRegime(colaborador) {
  const trocas = [];
  let anterior = colaborador.regime_atual;
  [1, 2].forEach((ordem) => {
    const r = reajusteAtual(colaborador, ordem);
    if (!r.ativo || !r.regime) return;
    if (anterior && r.regime !== anterior) trocas.push({ ordem, de: anterior, para: r.regime, mes: r.mes });
    anterior = r.regime;
  });
  return trocas;
}

// % de reajuste dentro do ano: Dez ÷ Jan − 1 sobre a série mensal da visão (salário, encargos,
// benefícios ou custo total). Quem entra ou sai no meio do ano tem Jan/Dez zerados — aí compara
// o primeiro com o último mês com valor. Sem 2 meses com valor, não há reajuste a medir (null).
function reajusteNoAno(meses) {
  const comValor = Array.from({ length: 12 }, (_, i) => i + 1).filter((m) => Number(meses?.[m] || 0) > 0);
  if (comValor.length < 2) return null;
  const mesInicio = comValor[0];
  const mesFim = comValor[comValor.length - 1];
  const inicio = Number(meses[mesInicio]);
  const fim = Number(meses[mesFim]);
  return { percentual: (fim / inicio - 1) * 100, mesInicio, mesFim, inicio, fim };
}

function rotuloReajusteNoAno(r) {
  if (!r) return "—";
  return `${r.percentual > 0.005 ? "+" : ""}${formatPercent(r.percentual)}`;
}

function custoTotalFolha(lista) {
  return lista.reduce((acc, c) => acc + Number(c.total_salario || 0) + Number(c.total_encargos || 0) + Number(c.total_beneficios || 0), 0);
}

// Cópia do colaborador usada como rascunho no modal de edição — reajuste_1/reajuste_2 vêm
// sempre normalizados (nunca undefined), para o diff de "o que mudou" na hora de salvar
// funcionar mesmo quando o colaborador nunca teve nenhum reajuste configurado.
function clonarColaborador(c) {
  return { ...c, sem_reajuste: !!c.sem_reajuste, reajuste_1: { ...reajusteAtual(c, 1) }, reajuste_2: { ...reajusteAtual(c, 2) } };
}

// Rascunho local (nunca gravado no backend) usado pelo modal ao clicar em "Nova contratação" —
// espelha exatamente os defaults que POST /orcamento/mao-de-obra aplicaria (ver
// routers/mao_de_obra.py:criar_colaborador), para que o diff feito em salvarEdicaoColaborador
// só capture os campos que o usuário de fato preencheu. Nada é criado no banco até o usuário
// alterar algo no modal e clicar em "Salvar alterações" (ver criarColaborador).
function novoColaboradorRascunho(departamento, ano) {
  return {
    id: null,
    departamento,
    cargo: "",
    nivel_cargo: "colaborador",
    plano_previdencia: false,
    modelo_trabalho: "presencial",
    agrupamento: 0,
    enquadramento: 1.00,
    regime_atual: "CLT",
    ocupante: null,
    salario_bruto_atual: 0,
    salario_excecao: false,
    vigencia_inicio: 1,
    vigencia_fim: 12,
    nova_contratacao: true,
    ano_referencia: ano,
    reajuste_1: { ordem: 1, ativo: false, cargo: null, agrupamento: null, enquadramento: null, regime: null, salario: null, salario_excecao: false, mes: null },
    reajuste_2: { ordem: 2, ativo: false, cargo: null, agrupamento: null, enquadramento: null, regime: null, salario: null, salario_excecao: false, mes: null },
    salario_bruto: 0,
    meses_salario: {}, meses_encargos: {}, meses_beneficios: {}, meses_custo_total: {},
    total_salario: 0, total_encargos: 0, total_beneficios: 0, total_custo_total: 0,
    composicao_encargos: [], composicao_beneficios: [],
  };
}

// Cargos já cadastrados (todos os departamentos que a role enxerga) — sugestões na Nova
// contratação para cargos que ainda não estão no Catálogo de Cargos do RH (ex.: Estágio, Diretor).
function cargosCadastrados(colaboradores) {
  const porCargo = new Map();
  colaboradores.forEach((c) => {
    const chave = chaveCargo(c.cargo);
    if (chave && !porCargo.has(chave)) porCargo.set(chave, c.cargo.trim());
  });
  return [...porCargo.values()];
}

function rotuloOpcao(opcoes, valor) {
  if (valor === null || valor === undefined || valor === "") return "—";
  const opcao = opcoes.find((o) => o.value === valor);
  return opcao ? opcao.label : String(valor);
}
const rotuloAgrupamento = (valor) => (valor === null || valor === undefined ? "—" : String(valor));
const rotuloEnquadramento = (valor) => (valor === null || valor === undefined ? "—" : formatPercent(valor * 100));
const rotuloBooleano = (valor) => (valor ? "Sim" : "Não");
const rotuloMoeda = (valor) => (valor === null || valor === undefined ? "—" : formatCurrency(valor));

// Definição única das colunas de atributos da tabela (exclui meses/Total/Ações, que são
// calculados) — usada para gerar o cabeçalho com filtros, a exportação completa para Excel e
// a lista de valores únicos de cada filtro, evitando manter 3 listas de colunas em paralelo.
const COLUNAS_TABELA = [
  { key: "departamento", label: "Departamento", tituloExcel: "Departamento", align: "left", getValor: (c) => c.departamento },
  { key: "cargo", label: "Cargo/Função", tituloExcel: "Cargo/Função", align: "left", sticky: true, width: 190, getValor: (c) => c.cargo || "" },
  { key: "nivel_cargo", label: "Nível Cargo", tituloExcel: "Nível Cargo", getValor: (c) => c.nivel_cargo, getRotulo: (v) => rotuloOpcao(NIVEIS_CARGO, v) },
  { key: "plano_previdencia", label: "Previdência", tituloExcel: "Previdência", getValor: (c) => !!c.plano_previdencia, getRotulo: rotuloBooleano },
  { key: "modelo_trabalho", label: "Modelo Trabalho", tituloExcel: "Modelo Trabalho", getValor: (c) => c.modelo_trabalho, getRotulo: (v) => rotuloOpcao(MODELOS_TRABALHO, v) },
  { key: "agrupamento", label: "Agrupamento", tituloExcel: "Agrupamento", getValor: (c) => c.agrupamento, getRotulo: rotuloAgrupamento },
  { key: "enquadramento", label: "Enquadramento", tituloExcel: "Enquadramento", getValor: (c) => c.enquadramento, getRotulo: rotuloEnquadramento },
  { key: "regime_atual", label: "Regime Atual", tituloExcel: "Regime Atual", getValor: (c) => c.regime_atual, getRotulo: (v) => rotuloOpcao(REGIMES_MAO_DE_OBRA, v) },
  { key: "ocupante", label: "Ocupante", tituloExcel: "Ocupante", align: "left", sticky: true, width: 170, getValor: (c) => c.ocupante || "", getRotulo: (v) => v || "Vaga em aberto" },
  { key: "salario_bruto_atual", label: "Salário Bruto Atual", tituloExcel: "Salário Bruto Atual", align: "right", sticky: true, width: 160, getValor: (c) => c.salario_bruto_atual, getRotulo: rotuloMoeda, getExcel: (v) => (v ?? "") },
  { key: "sem_reajuste", label: "Sem Reajuste", tituloExcel: "Sem Reajuste no Ano", reajuste: true, getValor: (c) => !!c.sem_reajuste, getRotulo: rotuloBooleano },
  { key: "r1_ativo", label: "Reajuste 1", tituloExcel: "Reajuste 1 - Ativo", reajuste: true, getValor: (c) => !!reajusteAtual(c, 1).ativo, getRotulo: rotuloBooleano },
  { key: "r1_cargo", label: "Cargo", tituloExcel: "Reajuste 1 - Novo Cargo", reajuste: true, getValor: (c) => reajusteAtual(c, 1).cargo || "", getRotulo: (v) => v || "—" },
  { key: "r1_agrupamento", label: "Agrupamento", tituloExcel: "Reajuste 1 - Agrupamento", reajuste: true, getValor: (c) => reajusteAtual(c, 1).agrupamento, getRotulo: rotuloAgrupamento },
  { key: "r1_enquadramento", label: "Enquadramento", tituloExcel: "Reajuste 1 - Enquadramento", reajuste: true, getValor: (c) => reajusteAtual(c, 1).enquadramento, getRotulo: rotuloEnquadramento },
  { key: "r1_regime", label: "Regime", tituloExcel: "Reajuste 1 - Regime", reajuste: true, getValor: (c) => reajusteAtual(c, 1).regime, getRotulo: (v) => rotuloOpcao(REGIMES_MAO_DE_OBRA, v) },
  { key: "r1_salario", label: "Salário", tituloExcel: "Reajuste 1 - Salário", reajuste: true, getValor: (c) => reajusteAtual(c, 1).salario, getRotulo: rotuloMoeda, getExcel: (v) => (v ?? "") },
  { key: "r1_mes", label: "Mês Início", tituloExcel: "Reajuste 1 - Mês Início", reajuste: true, getValor: (c) => reajusteAtual(c, 1).mes, getRotulo: (v) => rotuloOpcao(MESES_MAO_DE_OBRA, v) },
  { key: "r2_ativo", label: "Reajuste 2", tituloExcel: "Reajuste 2 - Ativo", reajuste: true, getValor: (c) => !!reajusteAtual(c, 2).ativo, getRotulo: rotuloBooleano },
  { key: "r2_cargo", label: "Cargo", tituloExcel: "Reajuste 2 - Novo Cargo", reajuste: true, getValor: (c) => reajusteAtual(c, 2).cargo || "", getRotulo: (v) => v || "—" },
  { key: "r2_agrupamento", label: "Agrupamento", tituloExcel: "Reajuste 2 - Agrupamento", reajuste: true, getValor: (c) => reajusteAtual(c, 2).agrupamento, getRotulo: rotuloAgrupamento },
  { key: "r2_enquadramento", label: "Enquadramento", tituloExcel: "Reajuste 2 - Enquadramento", reajuste: true, getValor: (c) => reajusteAtual(c, 2).enquadramento, getRotulo: rotuloEnquadramento },
  { key: "r2_regime", label: "Regime", tituloExcel: "Reajuste 2 - Regime", reajuste: true, getValor: (c) => reajusteAtual(c, 2).regime, getRotulo: (v) => rotuloOpcao(REGIMES_MAO_DE_OBRA, v) },
  { key: "r2_salario", label: "Salário", tituloExcel: "Reajuste 2 - Salário", reajuste: true, getValor: (c) => reajusteAtual(c, 2).salario, getRotulo: rotuloMoeda, getExcel: (v) => (v ?? "") },
  { key: "r2_mes", label: "Mês Atualização", tituloExcel: "Reajuste 2 - Mês Atualização", reajuste: true, getValor: (c) => reajusteAtual(c, 2).mes, getRotulo: (v) => rotuloOpcao(MESES_MAO_DE_OBRA, v) },
  { key: "salario_bruto", label: "Salário Bruto", tituloExcel: "Salário Bruto (Dez)", getValor: (c) => c.salario_bruto, getRotulo: rotuloMoeda, getExcel: (v) => (v ?? "") },
  { key: "vigencia_inicio", label: "Vigência Início", tituloExcel: "Vigência Início", getValor: (c) => c.vigencia_inicio, getRotulo: (v) => rotuloOpcao(MESES_MAO_DE_OBRA, v) },
  { key: "vigencia_fim", label: "Vigência Fim", tituloExcel: "Vigência Fim", getValor: (c) => c.vigencia_fim, getRotulo: (v) => rotuloOpcao(MESES_MAO_DE_OBRA, v) },
];

// Visão executiva: a tabela principal mostra só o essencial (Cargo, Ocupante, Salário Bruto
// Atual) + Total calculado — o resto (Departamento, classificação, reajustes, vigência,
// composição de custo) fica no modal "Editar" (ver ModalEditarColaborador), para não poluir
// a lista com ~20 colunas de uso ocasional. Departamento já está fixado pelo seletor da
// sidebar (1 departamento por vez), então sai da tabela — editável só no modal. COLUNAS_TABELA
// continua completa para a Exportação Excel, que é um dump de todos os dados independente do
// que está visível na tela.
const CHAVES_VISAO_EXECUTIVA = ["cargo", "ocupante", "salario_bruto_atual"];
const COLUNAS_VISAO_EXECUTIVA = COLUNAS_TABELA.filter((col) => CHAVES_VISAO_EXECUTIVA.includes(col.key));

// Cargo/Função, Ocupante e Salário Bruto Atual ficam "travados" (sticky) à esquerda ao rolar a
// tabela horizontalmente até os meses — cada um precisa de um `left` = soma das larguras dos
// sticky anteriores, por isso a largura de cada coluna sticky é fixa (não é só um `minWidth`).
let _offsetStickyAcumulado = 0;
COLUNAS_VISAO_EXECUTIVA.forEach((col) => {
  if (!col.sticky) return;
  col.stickyLeft = _offsetStickyAcumulado;
  _offsetStickyAcumulado += col.width;
});
const STICKY_POR_CHAVE = Object.fromEntries(COLUNAS_VISAO_EXECUTIVA.filter((c) => c.sticky).map((c) => [c.key, c]));

// Estilo de uma célula do corpo (não cabeçalho) travada na coluna `chave` — usa o mesmo
// left/width calculados acima para o cabeçalho, para as duas ficarem sempre alinhadas.
function stickyTdStyle(chave, background) {
  const col = STICKY_POR_CHAVE[chave];
  return { position: "sticky", left: col.stickyLeft, width: col.width, minWidth: col.width, maxWidth: col.width, background, zIndex: 1 };
}

// Valores únicos de uma coluna (para o dropdown de filtro), com rótulo pronto para exibição.
function valoresUnicosColuna(lista, coluna) {
  const mapa = new Map();
  lista.forEach((c) => {
    const valor = coluna.getValor(c);
    const chave = valor === null || valor === undefined ? "__vazio__" : String(valor);
    if (!mapa.has(chave)) {
      mapa.set(chave, { valor, rotulo: coluna.getRotulo ? coluna.getRotulo(valor) : (valor === "" || valor === null || valor === undefined ? "—" : String(valor)) });
    }
  });
  return Array.from(mapa.values()).sort((a, b) => a.rotulo.localeCompare(b.rotulo, "pt-BR"));
}

// Visão genérica de mão de obra: recebe `departamentos` (lista de departamentos deste módulo —
// ver DEPARTAMENTOS_CUSTO_MAO_DE_OBRA / DEPARTAMENTOS_DESPESAS_MAO_DE_OBRA em
// constants/departamentosOrcamento.js) e exibe 1 departamento por vez, selecionado no dropdown
// da sidebar — igual a Custos Gerais × Despesas Gerais — para nunca misturar colaboradores de
// departamentos diferentes na mesma tabela.
// O backend não filtra por departamento; a filtragem e os KPIs agregados são calculados aqui.
export default function MaoDeObraView({ tituloModulo, departamentos }) {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const navigate = useNavigate();

  const [ano, setAno] = useState(ANO_ATUAL+1);
  const [departamento, setDepartamento] = useState(departamentos?.[0]);
  const [colaboradores, setColaboradores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");

  const [visao, setVisao] = useState("salario");
  const [colaboradorEditandoId, setColaboradorEditandoId] = useState(null);
  // Rascunho de contratação nova ainda não gravada — ver novoColaboradorRascunho/criarColaborador.
  const [criandoNovo, setCriandoNovo] = useState(false);
  const [tabelaSalarialAberta, setTabelaSalarialAberta] = useState(false);

  // Filtros por coluna, estilo Excel: { [chaveColuna]: Set com os valores marcados }. Ausência
  // da chave (ou undefined) significa "sem filtro" — mostra todos os valores daquela coluna.
  const [filtros, setFiltros] = useState({});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setFiltros({}); }, [departamento, ano]);

  function atualizarFiltro(chave, novoConjunto) {
    setFiltros((f) => {
      const copia = { ...f };
      if (!novoConjunto) delete copia[chave]; else copia[chave] = novoConjunto;
      return copia;
    });
  }

  const cargosExistentes = useMemo(() => cargosCadastrados(colaboradores), [colaboradores]);

  const colaboradoresFiltrados = useMemo(
    () => colaboradores.filter((c) => c.departamento === departamento),
    [colaboradores, departamento]
  );

  const valoresPorColuna = useMemo(
    () => Object.fromEntries(COLUNAS_VISAO_EXECUTIVA.map((col) => [col.key, valoresUnicosColuna(colaboradoresFiltrados, col)])),
    [colaboradoresFiltrados]
  );

  const filtrosAtivos = Object.keys(filtros).length;

  // Ordenação por coluna (Cargo, Ocupante, Salário): clique no título alterna crescente →
  // decrescente → sem ordenação (ordem original do backend). Vazios sempre ficam no fim.
  const [ordenacao, setOrdenacao] = useState(null); // { chave, direcao: "asc" | "desc" }

  function alternarOrdenacao(chave) {
    setOrdenacao((o) => {
      if (!o || o.chave !== chave) return { chave, direcao: "asc" };
      if (o.direcao === "asc") return { chave, direcao: "desc" };
      return null;
    });
  }

  const colaboradoresExibidos = useMemo(() => {
    const lista = colaboradoresFiltrados.filter((c) => COLUNAS_TABELA.every((col) => {
      const marcados = filtros[col.key];
      return !marcados || marcados.has(col.getValor(c));
    }));
    if (!ordenacao) return lista;
    const col = COLUNAS_TABELA.find((cl) => cl.key === ordenacao.chave);
    const sinal = ordenacao.direcao === "asc" ? 1 : -1;
    const vazio = (v) => v === null || v === undefined || v === "";
    return [...lista].sort((a, b) => {
      const va = col.getValor(a);
      const vb = col.getValor(b);
      if (vazio(va) || vazio(vb)) return vazio(va) - vazio(vb);
      const cmp = col.align === "right"
        ? Number(va) - Number(vb)
        : String(va).localeCompare(String(vb), "pt-BR", { sensitivity: "base", numeric: true });
      return cmp * sinal;
    });
  }, [colaboradoresFiltrados, filtros, ordenacao]);

  // Barra de rolagem horizontal duplicada acima da tabela, sincronizada com a rolagem real —
  // com muitas colunas, o usuário não precisa descer até o fim da tabela para rolar.
  const tabelaScrollRef = useRef(null);
  const topoScrollRef = useRef(null);
  const [larguraTabela, setLarguraTabela] = useState(0);

  useEffect(() => {
    const el = tabelaScrollRef.current;
    if (!el) return;
    const atualizarLargura = () => setLarguraTabela(el.scrollWidth);
    atualizarLargura();
    const obs = new ResizeObserver(atualizarLargura);
    obs.observe(el);
    return () => obs.disconnect();
  }, [colaboradoresExibidos, visao]);

  function sincronizarDoTopo() {
    if (tabelaScrollRef.current && topoScrollRef.current) tabelaScrollRef.current.scrollLeft = topoScrollRef.current.scrollLeft;
  }
  function sincronizarDaTabela() {
    if (tabelaScrollRef.current && topoScrollRef.current) topoScrollRef.current.scrollLeft = tabelaScrollRef.current.scrollLeft;
  }

  async function carregarTabela() {
    setLoading(true);
    setErro("");
    try {
      const lista = await getColaboradoresMaoDeObra(ano);
      setColaboradores(lista || []);
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregarTabela(); }, [ano]);

  // Dados do ano anterior — usados pelos KPIs de Custo Total ano anterior / Variação Anual.
  const [colaboradoresAnoAnterior, setColaboradoresAnoAnterior] = useState([]);
  useEffect(() => {
    let cancelado = false;
    getColaboradoresMaoDeObra(ano - 1)
      .then((lista) => { if (!cancelado) setColaboradoresAnoAnterior(lista || []); })
      .catch(() => { if (!cancelado) setColaboradoresAnoAnterior([]); });
    return () => { cancelado = true; };
  }, [ano]);

  // Colaborador é contínuo entre anos (mesmo id) — base da coluna "% Reajuste" do ano anterior.
  const anoAnteriorPorId = useMemo(
    () => new Map(colaboradoresAnoAnterior.map((c) => [c.id, c])),
    [colaboradoresAnoAnterior]
  );

  function atualizarColaboradorLocal(atualizado) {
    setColaboradores((lista) => lista.map((c) => (c.id === atualizado.id ? atualizado : c)));
  }

  async function salvarCampo(colaborador, campo, valor) {
    try {
      const atualizado = await putColaboradorMaoDeObra(colaborador.id, { [campo]: valor }, ano);
      atualizarColaboradorLocal(atualizado);
    } catch (e) {
      setErro(e.message);
    }
  }

  // Campos "de classificação" editados no modal — Cargo/Ocupante/Salário Bruto Atual ficam na
  // linha da tabela (LinhaColaborador) e continuam salvando direto via salvarCampo, pois lá o
  // usuário já está preenchendo o dado real. O modal, por outro lado, muitas vezes é usado para
  // simular cenários (ex: "e se esse cargo virasse PJ?") — por isso ele NÃO salva a cada campo:
  // acumula tudo em um rascunho local e só grava (via salvarEdicaoColaborador) quando o usuário
  // clica em "Salvar alterações", evitando perder o dado real de uma simulação abandonada.
  // Cargo só é editável no modal em modo criação (Nova contratação); na edição não muda, então
  // nunca entra no diff.
  const CAMPOS_CLASSIFICACAO_MODAL = [
    "cargo", "departamento", "nivel_cargo", "modelo_trabalho", "agrupamento",
    "enquadramento", "regime_atual", "plano_previdencia", "vigencia_inicio", "vigencia_fim",
    "nova_contratacao", "salario_excecao", "salario_bruto_atual",
  ];

  // `kit` (opcional) = { kit_admissao: payload | null } quando o Kit Admissão vinculado mudou na
  // modal — vai no mesmo PUT (o backend refaz ou remove o kit; ver mao_de_obra.atualizar_colaborador).
  async function salvarEdicaoColaborador(original, draft, kit = null) {
    const patch = { ...(kit || {}) };
    CAMPOS_CLASSIFICACAO_MODAL.forEach((campo) => {
      if (JSON.stringify(draft[campo]) !== JSON.stringify(original[campo])) patch[campo] = draft[campo];
    });
    // Fora da exceção o backend trava o salário na tabela salarial — não adianta enviar.
    if (!draft.salario_excecao) delete patch.salario_bruto_atual;
    if (Object.keys(patch).length > 0) {
      const atualizado = await putColaboradorMaoDeObra(original.id, patch, ano);
      atualizarColaboradorLocal(atualizado);
    }
    for (const ordem of [1, 2]) {
      const antes = reajusteAtual(original, ordem);
      const depois = reajusteAtual(draft, ordem);
      if (JSON.stringify(antes) !== JSON.stringify(depois)) {
        const atualizado = await putReajusteMaoDeObra(original.id, ordem, depois, ano);
        atualizarColaboradorLocal(atualizado);
      }
    }
    if (!!draft.sem_reajuste !== !!original.sem_reajuste) {
      const atualizado = await putSemReajusteMaoDeObra(original.id, !!draft.sem_reajuste, ano);
      atualizarColaboradorLocal(atualizado);
    }
  }

  // Abre o modal com um rascunho local (novoColaboradorRascunho) — nada é gravado no backend
  // aqui. Antes o clique já disparava o POST e a linha entrava "solta" na tabela com os defaults
  // do backend (inclusive vigência início sempre Janeiro) mesmo se o usuário fechasse o modal sem
  // preencher nada. Agora o POST só acontece dentro de criarColaborador, quando o usuário altera
  // algo no modal e clica em "Salvar alterações" (mesmo botão que fica desabilitado até haver
  // alteração — ver `alterado` em ModalEditarColaborador).
  function novaLinha() {
    setColaboradorEditandoId(null);
    setCriandoNovo(true);
  }

  // Só chamada a partir do "Salvar alterações" do modal em modo criação: primeiro cria a linha
  // com os defaults do backend, depois aplica (via salvarEdicaoColaborador) só o que o usuário
  // de fato alterou em relação ao rascunho — reaproveitando o mesmo diff usado na edição normal.
  // `kitAdmissao` (etapas Kit Admissão / Assinaturas e Sistemas) vai no mesmo POST: o backend
  // cria colaborador e kit numa transação só (ver kit_admissao_svc.criar_kit_admissao_contratacao).
  async function criarColaborador(draft, kitAdmissao) {
    const criado = await postColaboradorMaoDeObra({
      ano_referencia: ano, departamento: draft.departamento || departamento, kit_admissao: kitAdmissao || null,
    });
    setColaboradores((lista) => [...lista, criado]);
    await salvarEdicaoColaborador(criado, draft);
  }

  // Colaborador que veio de ano anterior é desligado a partir do ano da tela (o histórico fica);
  // só no ano de entrada a linha é excluída de vez — e aí some de todos os anos.
  async function excluirLinha(colaborador) {
    const nome = `"${colaborador.cargo || "colaborador"}" (${colaborador.ocupante || "vaga em aberto"})`;
    const mensagem = colaborador.ano_entrada < ano
      ? `Desligar ${nome} a partir de ${ano}? Ele deixa de aparecer em ${ano} e nos anos seguintes; os dados de ${colaborador.ano_entrada} a ${ano - 1} são mantidos.`
      : `Excluir a linha de ${nome}? Ela será removida de ${ano} e de todos os anos seguintes${colaborador.nova_contratacao ? ", junto com o Kit Admissão e as assinaturas lançados para esta contratação" : ""}. Essa ação não pode ser desfeita.`;
    if (!window.confirm(mensagem)) return;
    try {
      await deleteColaboradorMaoDeObra(colaborador.id, ano);
      setColaboradores((lista) => lista.filter((c) => c.id !== colaborador.id));
    } catch (e) {
      setErro(e.message);
    }
  }

  // Sempre relido da lista (não guardado por referência) para refletir a versão mais recente
  // (ex: campos calculados) assim que o modal salvar e a tabela recarregar.
  const colaboradorEmEdicao = colaboradorEditandoId ? colaboradores.find((c) => c.id === colaboradorEditandoId) || null : null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rascunhoNovo = useMemo(() => (criandoNovo ? novoColaboradorRascunho(departamento, ano) : null), [criandoNovo]);

  const visaoAtual = VISOES_MAO_DE_OBRA.find((v) => v.id === visao) || VISOES_MAO_DE_OBRA[3];

  const totaisPorMes = useMemo(() => {
    const totais = Array(12).fill(0);
    colaboradoresExibidos.forEach((c) => {
      const meses = c[visaoAtual.campoMensal] || {};
      for (let mes = 1; mes <= 12; mes++) totais[mes - 1] += Number(meses[mes] || 0);
    });
    return totais;
  }, [colaboradoresExibidos, visaoAtual]);
  const totalGeralVisao = totaisPorMes.reduce((acc, v) => acc + v, 0);

  function exportarParaExcel() {
    const linhas = colaboradoresExibidos.map((c) => {
      const linha = {};
      COLUNAS_TABELA.forEach((col) => {
        const valor = col.getValor(c);
        linha[col.tituloExcel] = col.getExcel ? col.getExcel(valor) : (col.getRotulo ? col.getRotulo(valor) : (valor ?? ""));
      });
      const meses = c[visaoAtual.campoMensal] || {};
      const anterior = anoAnteriorPorId.get(c.id);
      const rAnterior = anterior ? reajusteNoAno(anterior[visaoAtual.campoMensal]) : null;
      const rAtual = reajusteNoAno(meses);
      linha[`% Reajuste ${ano - 1}`] = rAnterior ? Number(rAnterior.percentual.toFixed(2)) : "";
      linha[`% Reajuste ${ano}`] = rAtual ? Number(rAtual.percentual.toFixed(2)) : "";
      MESES_ABREV.forEach((m, i) => { linha[m] = Number(meses[i + 1] || 0); });
      linha["Total"] = c[visaoAtual.campoTotal];
      return linha;
    });
    linhas.push({
      ...Object.fromEntries(COLUNAS_TABELA.map((col) => [col.tituloExcel, ""])),
      "Cargo/Função": "Total Geral",
      ...Object.fromEntries(MESES_ABREV.map((m, i) => [m, totaisPorMes[i]])),
      Total: totalGeralVisao,
    });
    const worksheet = XLSX.utils.json_to_sheet(linhas);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, tituloModulo.slice(0, 31));
    XLSX.writeFile(workbook, `${tituloModulo}_${departamento}_${visaoAtual.label}_${ano}`.replace(/[^\w]+/g, "_") + ".xlsx");
  }

  // KPIs comparam sempre o departamento inteiro do ano (colaboradoresFiltrados), sem sofrer
  // efeito dos filtros de coluna da tabela.
  const colaboradoresAnoAnteriorFiltrados = useMemo(
    () => colaboradoresAnoAnterior.filter((c) => c.departamento === departamento),
    [colaboradoresAnoAnterior, departamento]
  );
  const custoAnoAtual = useMemo(() => custoTotalFolha(colaboradoresFiltrados), [colaboradoresFiltrados]);
  const custoAnoAnterior = useMemo(() => custoTotalFolha(colaboradoresAnoAnteriorFiltrados), [colaboradoresAnoAnteriorFiltrados]);
  const totalColaboradoresDespesas = colaboradoresFiltrados.length;
  const colaboradoresComReajuste = useMemo(() => colaboradoresFiltrados.filter(reajustePreenchido).length, [colaboradoresFiltrados]);
  const progressoSubmissao = totalColaboradoresDespesas > 0 ? (colaboradoresComReajuste / totalColaboradoresDespesas) * 100 : 0;
  const variacaoAnual = custoAnoAtual - custoAnoAnterior;
  const direcaoVariacaoAnual = variacaoAnual > 0 ? "up" : variacaoAnual < 0 ? "down" : "flat";
  const percentVariacaoAnual = custoAnoAnterior > 0 ? (variacaoAnual / custoAnoAnterior) * 100 : 0;

  const kpiItems = [
    { label: `Custo Total Orçado ${ano}`, value: loading ? "—" : formatCurrency(custoAnoAtual), background: C.brand },
    { label: `Custo Total ${ano - 1}`, value: loading ? "—" : formatCurrency(custoAnoAnterior), color: C.muted },
    {
      label: "Progresso Orçamentário", value: loading ? "—" : `${colaboradoresComReajuste}/${totalColaboradoresDespesas}`,
      sub: "colaboradores com reajuste informado",
      accessory: !loading && <GaugeProgresso percent={progressoSubmissao} color={C.brand} trackColor={C.border} />,
    },
    {
      label: "Variação Anual",
      value: loading ? "—" : `${variacaoAnual > 0 ? "+" : ""}${formatCurrency(variacaoAnual)}`,
      color: direcaoVariacaoAnual === "up" ? C.success : direcaoVariacaoAnual === "down" ? C.danger : C.text,
      sub: loading ? undefined : `vs. ${ano - 1}: ${variacaoAnual === 0 ? "Sem alteração" : `${percentVariacaoAnual > 0 ? "+" : ""}${percentVariacaoAnual.toFixed(1)}%`}`,
      accessory: !loading && <TrendAccessory direcao={direcaoVariacaoAnual} C={C} />,
    },
  ];

  if (!departamentos || departamentos.length === 0) {
    return (
      <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif" }}>
        <SideBarNavGestaoOrcamentaria isLight={isLight} />
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted, fontSize: 14 }}>
          Nenhum departamento disponível para o seu perfil em {tituloModulo}.
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text }}>
      <SideBarNavGestaoOrcamentaria
        isLight={isLight}
        departamentos={departamentos}
        departamentoAtual={departamento}
        onSelecionarDepartamento={setDepartamento}
      />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
        <header style={{
          background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)", color: "#fff", padding: "14px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.8, marginLeft: 8 }}>· {tituloModulo}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 10px", fontSize: 13, fontWeight: 600, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={departamento}>
              {departamento}
            </span>
            <select value={ano} onChange={(e) => setAno(parseInt(e.target.value, 10))}
              style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 10px", color: "#fff", fontSize: 13, fontWeight: 600 }}>
              {OPCOES_ANO.map((a) => <option key={a} value={a} style={{ color: "#0F172A" }}>{a}</option>)}
            </select>
            <button onClick={() => setIsLight((l) => !l)} style={headerBtnStyle}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />} {isLight ? "Tema Claro" : "Tema Escuro"}
            </button>
            <button onClick={() => navigate("/inicio")} style={headerBtnStyle}>Menu Principal</button>
          </div>
        </header>

        <main style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24 }}>
          {erro && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: `${C.danger}18`, border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 10, padding: "10px 14px", fontSize: 13 }}>
              <AlertTriangle size={16} /> {erro}
            </div>
          )}

          <KpiRowOrcamento items={kpiItems} C={C} />

          <section>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text }}>{tituloModulo} · {departamento} · {ano}</h2>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <button onClick={() => setTabelaSalarialAberta(true)} title={`Tabela salarial ${ano} (Agrupamento × Enquadramento)`}
                  style={btnStyle(C.brand, true)}>
                  <Table2 size={15} /> Tabela Salarial
                </button>
                <button onClick={exportarParaExcel} disabled={loading || colaboradoresExibidos.length === 0} title="Exportar dados para Excel (todas as colunas)"
                  style={{ ...btnStyle(C.success, true), opacity: loading || colaboradoresExibidos.length === 0 ? 0.45 : 1, cursor: loading || colaboradoresExibidos.length === 0 ? "not-allowed" : "pointer" }}>
                  <FileSpreadsheet size={15} /> Exportar Excel
                </button>
                {filtrosAtivos > 0 && (
                  <button onClick={() => setFiltros({})} title="Remover todos os filtros de coluna"
                    style={{ ...btnStyle(C.danger, true), padding: "9px 12px" }}>
                    <X size={14} /> Limpar filtros ({filtrosAtivos})
                  </button>
                )}
                <div style={{ display: "flex", gap: 6, background: C.bg, padding: 4, borderRadius: 10, border: `1px solid ${C.border}` }}>
                  {VISOES_MAO_DE_OBRA.map((v) => (
                    <button key={v.id} onClick={() => setVisao(v.id)} style={{
                      padding: "7px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                      background: visao === v.id ? C.brand : "transparent", color: visao === v.id ? "#fff" : C.text,
                      fontSize: 12, fontWeight: 700, fontFamily: "inherit",
                    }}>
                      {v.label}
                    </button>
                  ))}
                </div>
                <button onClick={novaLinha} style={btnStyle(C.brand)}><Plus size={15} /> Nova contratação</button>
              </div>
            </div>

            {loading ? (
              <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13 }}>Carregando…</div>
            ) : colaboradoresFiltrados.length === 0 ? (
              <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14 }}>
                Nenhum colaborador cadastrado para {departamento} em {ano}. Clique em "Nova linha" para começar.
              </div>
            ) : (
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
                <div ref={topoScrollRef} onScroll={sincronizarDoTopo} style={{ overflowX: "auto", overflowY: "hidden", height: 14 }}>
                  <div style={{ width: larguraTabela, height: 1 }} />
                </div>
                <div ref={tabelaScrollRef} onScroll={sincronizarDaTabela} style={{ overflow: "auto", maxHeight: "65vh" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 1700 }}>
                    <thead>
                      <tr>
                        <th style={thStyle(C)}>Ações</th>
                        {COLUNAS_VISAO_EXECUTIVA.map((col) => (
                          <Fragment key={col.key}>
                            <th style={col.sticky
                              ? { ...thStyle(C, col.reajuste, col.align), position: "sticky", left: col.stickyLeft, top: 0, zIndex: 4, width: col.width, minWidth: col.width, maxWidth: col.width }
                              : thStyle(C, col.reajuste, col.align)}>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
                                <button onClick={() => alternarOrdenacao(col.key)} title={`Ordenar por ${col.label}`}
                                  style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: "none", padding: 0, cursor: "pointer", color: "inherit", font: "inherit" }}>
                                  {col.label}
                                  {ordenacao?.chave === col.key
                                    ? (ordenacao.direcao === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />)
                                    : <ArrowUpDown size={12} style={{ opacity: 0.5 }} />}
                                </button>
                                <FiltroColuna C={C} valores={valoresPorColuna[col.key] || []} selecionados={filtros[col.key]}
                                  onChange={(novo) => atualizarFiltro(col.key, novo)} />
                              </span>
                            </th>
                            {col.key === "cargo" && (
                              <th style={thStyle(C)}>Status Reajuste</th>
                            )}
                            {col.key === "ocupante" && [ano - 1, ano].map((a) => (
                              <th key={a} style={thStyle(C, true, "right")}
                                title={`${visaoAtual.label}: variação de Jan a Dez de ${a} (ou do 1º ao último mês com valor, se entrou/saiu no meio do ano)`}>
                                % Reajuste {a}
                              </th>
                            ))}
                          </Fragment>
                        ))}
                        {MESES_ABREV.map((m) => <th key={m} style={thStyle(C, false, "right")}>{m}</th>)}
                        <th style={thStyle(C, false, "right")}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Sem linhas após filtrar (ex: "Nenhum"), a tabela continua na tela para o
                          cabeçalho — e os filtros nele — seguirem acessíveis. */}
                      {colaboradoresExibidos.length === 0 && (
                        <tr>
                          <td colSpan={COLUNAS_VISAO_EXECUTIVA.length + 17} style={{ padding: 0 }}>
                            <div style={{ position: "sticky", left: 0, width: "min(100%, 90vw)", padding: 32, textAlign: "center", color: C.muted, fontSize: 13, boxSizing: "border-box" }}>
                              Nenhum colaborador corresponde aos filtros aplicados.{" "}
                              <button onClick={() => setFiltros({})} style={{ color: C.brand, background: "none", border: "none", cursor: "pointer", fontWeight: 700, fontSize: 13, fontFamily: "inherit" }}>Limpar filtros</button>
                            </div>
                          </td>
                        </tr>
                      )}
                      {colaboradoresExibidos.map((c, idx) => (
                        <LinhaColaborador
                          key={c.id} C={C} colaborador={c} idx={idx} visaoAtual={visaoAtual}
                          colaboradorAnoAnterior={anoAnteriorPorId.get(c.id)}
                          onSalvarCampo={(campo, valor) => salvarCampo(c, campo, valor)}
                          onEditar={() => setColaboradorEditandoId(c.id)}
                          onExcluir={() => excluirLinha(c)}
                        />
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ fontWeight: 800, borderTop: `2px solid ${C.border}` }}>
                        <td style={tdStyle(C)} />
                        <td style={{ ...tdStyle(C, true), position: "sticky", left: 0, textAlign: "left" }} colSpan={COLUNAS_VISAO_EXECUTIVA.length + 3}>Total Geral · {visaoAtual.label}</td>
                        {totaisPorMes.map((v, i) => <td key={i} style={tdStyle(C, true)}>{formatCurrency(v)}</td>)}
                        <td style={tdStyle(C, true)}>{formatCurrency(totalGeralVisao)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </section>
        </main>
      </div>

      {tabelaSalarialAberta && (
        <ModalTabelaSalarial C={C} ano={ano} onClose={() => setTabelaSalarialAberta(false)} />
      )}

      {(colaboradorEmEdicao || rascunhoNovo) && (
        <ModalEditarColaborador
          key={colaboradorEmEdicao ? colaboradorEmEdicao.id : "novo"}
          C={C} colaborador={colaboradorEmEdicao || rascunhoNovo} novo={!colaboradorEmEdicao}
          departamentos={[departamento]} visaoAtual={visaoAtual} ano={ano} cargosExistentes={cargosExistentes}
          onClose={() => { setColaboradorEditandoId(null); setCriandoNovo(false); }}
          onSalvar={(draft, kitAdmissao, kitAlterado) => (colaboradorEmEdicao
            ? salvarEdicaoColaborador(colaboradorEmEdicao, draft, kitAlterado ? { kit_admissao: kitAdmissao } : null)
            : criarColaborador(draft, kitAdmissao))}
        />
      )}
    </div>
  );
}

// Tabela salarial gravada = CLT (Estágio usa a mesma); PJ = CLT × (1 + adicional_pj%).
function fatorRegime(tabela, regime) {
  return regime === "PJ" ? 1 + Number(tabela?.adicional_pj || 0) / 100 : 1;
}
const formatarPercentualPj = (v) => `${Number(v || 0).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`;

// ── Modal: Tabela Salarial (Agrupamento × Enquadramento) ─────────────────────
// Somente leitura — a edição fica em Premissas Mão de Obra (admin). Mostra a tabela do ano
// selecionado no cabeçalho (própria ou herdada do último ano cadastrado + índice de reajuste).

function ModalTabelaSalarial({ C, ano, onClose }) {
  const [tabela, setTabela] = useState(null);
  const [erro, setErro] = useState("");
  const [regime, setRegime] = useState("CLT");

  useEffect(() => {
    let ativo = true;
    getTabelaSalarialMaoDeObra(ano)
      .then((t) => { if (ativo) setTabela(t); })
      .catch((e) => { if (ativo) setErro(e.message); });
    return () => { ativo = false; };
  }, [ano]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const itens = tabela?.itens || [];
  const agrupamentos = [...new Set(itens.map((i) => Number(i.agrupamento)))].sort((a, b) => a - b);
  const enquadramentos = [...new Set(itens.map((i) => Number(i.enquadramento)))].sort((a, b) => a - b);
  const fator = fatorRegime(tabela, regime);
  const valorDe = (agr, enq) => {
    const v = itens.find((i) => Number(i.agrupamento) === agr && Math.abs(Number(i.enquadramento) - enq) < 0.001)?.valor_referencia;
    return v == null ? null : Number(v) * fator;
  };
  const origem = !tabela ? "" : tabela.propria
    ? `Própria de ${ano}`
    : `Herdada de ${tabela.ano_origem}${ano > tabela.ano_origem ? " + índice de reajuste" : ""}`;
  const cel = { padding: "6px 8px", fontSize: 11.5, textAlign: "right", whiteSpace: "nowrap", borderBottom: `1px solid ${C.border}` };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24, width: "100%", maxWidth: 1100, boxShadow: "0 24px 64px rgba(0,0,0,0.4)", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, gap: 12 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <Table2 size={17} color={C.brand} />
              <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>Tabela Salarial {ano}</p>
              {tabela && (
                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, letterSpacing: "0.04em",
                  color: tabela.propria ? "#fff" : C.muted, background: tabela.propria ? C.success : C.bg, border: tabela.propria ? "none" : `1px solid ${C.border}` }}>
                  {origem}
                </span>
              )}
            </div>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: C.muted }}>
              Salário base (R$/mês, valor de janeiro) por Agrupamento (linhas) × Enquadramento (colunas). Edição em Premissas Mão de Obra.
              {tabela && regime === "PJ" && ` Tabela PJ = CLT + ${formatarPercentualPj(tabela.adicional_pj)}.`}
            </p>
            <div style={{ display: "inline-flex", gap: 4, marginTop: 10, background: C.bg, padding: 3, borderRadius: 8, border: `1px solid ${C.border}` }}>
              {[{ value: "CLT", label: "CLT / Estágio" }, { value: "PJ", label: tabela ? `PJ (+${formatarPercentualPj(tabela.adicional_pj)})` : "PJ" }].map((r) => (
                <button key={r.value} onClick={() => setRegime(r.value)} style={{
                  padding: "5px 12px", borderRadius: 6, border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 700,
                  background: regime === r.value ? C.brand : "transparent", color: regime === r.value ? "#fff" : C.text,
                }}>{r.label}</button>
              ))}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, display: "flex" }}>
            <X size={20} />
          </button>
        </div>

        {erro ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: `${C.danger}18`, border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 10, padding: "10px 14px", fontSize: 13 }}>
            <AlertTriangle size={16} /> {erro}
          </div>
        ) : !tabela ? (
          <div style={{ padding: 32, textAlign: "center", color: C.muted, fontSize: 13 }}>Carregando tabela…</div>
        ) : itens.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center", color: C.muted, fontSize: 13 }}>Nenhuma tabela salarial cadastrada para {ano}.</div>
        ) : (
          <div style={{ overflow: "auto", border: `1px solid ${C.border}`, borderRadius: 10 }}>
            <table style={{ borderCollapse: "collapse", width: "100%" }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle(C, false, "left"), left: 0, zIndex: 3 }}>Agrup. / Enq.</th>
                  {enquadramentos.map((enq) => (
                    <th key={enq} style={thStyle(C, false, "right")}>{Math.round(enq * 100)}%</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {agrupamentos.map((agr, idx) => (
                  <tr key={agr} style={{ background: idx % 2 === 0 ? C.card : C.rowAlt }}>
                    <td style={{ ...cel, textAlign: "left", fontWeight: 700, color: C.text, position: "sticky", left: 0, background: "inherit" }}>{agr}</td>
                    {enquadramentos.map((enq) => {
                      const v = valorDe(agr, enq);
                      return (
                        <td key={enq} style={{ ...cel, color: enq === 1 ? C.brand : C.text, fontWeight: enq === 1 ? 700 : 400 }}>
                          {v == null ? "—" : formatCurrency(v)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Linha da tabela (visão executiva) ────────────────────────────────────────
// Só o essencial fica na linha: Cargo, Ocupante e Salário Bruto Atual (editáveis, travados à
// esquerda ao rolar) + os meses e o Total calculado. Departamento, classificação, reajustes,
// vigência e composição de custo ficam no modal "Editar" (ver ModalEditarColaborador), aberto
// pelo ícone de lápis.

function LinhaColaborador({ C, colaborador: c, colaboradorAnoAnterior, idx, visaoAtual, onSalvarCampo, onEditar, onExcluir }) {
  const bgLinha = idx % 2 === 0 ? C.card : C.rowAlt;
  const meses = c[visaoAtual.campoMensal] || {};
  const reajustes = [
    colaboradorAnoAnterior ? reajusteNoAno(colaboradorAnoAnterior[visaoAtual.campoMensal]) : null,
    reajusteNoAno(meses),
  ];

  return (
    <tr>
      <td style={{ ...tdStyle(C), background: bgLinha }}>
        <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
          <button onClick={onEditar} title="Editar detalhes" style={iconBtnStyle(C)}><Pencil size={13} /></button>
          <button onClick={onExcluir} title="Excluir linha" style={iconBtnStyle(C, true)}><Trash2 size={13} /></button>
        </div>
      </td>
      <td style={{ ...tdStyle(C), ...stickyTdStyle("cargo", bgLinha), textAlign: "left" }}>
        <CampoTexto C={C} valor={c.cargo} onSalvar={(v) => onSalvarCampo("cargo", v)} larguraMin={170} />
        {promocoesNoAno(c).map((p) => (
          <div key={p.ordem} title={`Reajuste ${p.ordem}: passa a ${p.cargo} a partir de ${MESES_ABREV[p.mes - 1]}`}
            style={{ fontSize: 9.5, fontWeight: 700, color: C.brand, padding: "0 7px 4px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 190 }}>
            → {p.cargo} ({MESES_ABREV[p.mes - 1]})
          </div>
        ))}
      </td>
      <td style={{ ...tdStyle(C), background: bgLinha, textAlign: "center" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
          <BadgeStatusReajuste C={C} preenchido={reajustePreenchido(c)} semReajuste={!!c.sem_reajuste} />
          {trocasDeRegime(c).map((t) => <BadgeTrocaRegime key={t.ordem} C={C} troca={t} />)}
          {!reajustePreenchido(c) && (
            <span style={{ fontSize: 9.5, color: C.muted }}>Clique em editar para inserir ou marcar sem reajuste</span>
          )}
          {c.projetado && (
            <span style={{ fontSize: 9.5, color: C.muted, fontStyle: "italic" }}
              title="Nada cadastrado neste ano — valores carregados do último ano conhecido (corrigidos pelo índice de reajuste, se houver).">
              Projetado · desde {c.ano_entrada}
            </span>
          )}
        </div>
      </td>
      <td style={{ ...tdStyle(C), ...stickyTdStyle("ocupante", bgLinha), textAlign: "left" }}>
        <CampoTexto C={C} valor={c.ocupante || ""} onSalvar={(v) => onSalvarCampo("ocupante", v || null)} placeholder="Vaga em aberto" larguraMin={150} />
      </td>
      {reajustes.map((r, i) => (
        <td key={i} style={{ ...tdStyle(C), background: bgLinha, fontWeight: 600, color: !r || Math.abs(r.percentual) < 0.005 ? C.muted : r.percentual > 0 ? C.success : C.danger }}
          title={r ? `${MESES_ABREV[r.mesInicio - 1]} ${formatCurrency(r.inicio)} → ${MESES_ABREV[r.mesFim - 1]} ${formatCurrency(r.fim)}` : "Sem dois meses com valor para comparar"}>
          {rotuloReajusteNoAno(r)}
        </td>
      ))}
      <td style={{ ...tdStyle(C), ...stickyTdStyle("salario_bruto_atual", bgLinha), textAlign: "right" }}>
        <CampoSalarioTabela C={C} valor={c.salario_bruto_atual} excecao={c.salario_excecao}
          onSalvarValor={(v) => onSalvarCampo("salario_bruto_atual", v)}
          onToggleExcecao={(v) => onSalvarCampo("salario_excecao", v)} />
      </td>
      {Array.from({ length: 12 }, (_, i) => i + 1).map((mes) => (
        <td key={mes} style={{ ...tdStyle(C), background: bgLinha, color: C.muted, cursor: "not-allowed" }} title="Calculado automaticamente">
          {formatCurrency(meses[mes] || 0)}
        </td>
      ))}
      <td style={{ ...tdStyle(C), background: bgLinha, fontWeight: 700 }}>{formatCurrency(c[visaoAtual.campoTotal] || 0)}</td>
    </tr>
  );
}

// Badge "Status Reajuste" — verde "Inserido" quando o colaborador já tem Reajuste 1 e/ou 2
// preenchido (ou "Sem reajuste", quando marcado que não terá reajuste no ano), vermelho
// "Não inserido" caso contrário.
function BadgeStatusReajuste({ C, preenchido, semReajuste }) {
  const cor = preenchido ? C.success : C.danger;
  return (
    <span style={{
      display: "inline-block", padding: "3px 9px", borderRadius: 999, fontSize: 10.5, fontWeight: 700,
      background: `${cor}22`, color: cor, whiteSpace: "nowrap",
    }}>
      {semReajuste ? "Sem reajuste" : preenchido ? "Inserido" : "Não inserido"}
    </span>
  );
}

// Alerta de troca de regime junto ao Status Reajuste: "CLT → PJ · Jul". Muda encargos e
// benefícios a partir do mês do reajuste, por isso fica destacado em âmbar.
function BadgeTrocaRegime({ C, troca }) {
  const de = rotuloOpcao(REGIMES_MAO_DE_OBRA, troca.de);
  const para = rotuloOpcao(REGIMES_MAO_DE_OBRA, troca.para);
  const mes = troca.mes ? MESES_ABREV[troca.mes - 1] : null;
  return (
    <span
      title={`Troca de regime no Reajuste ${troca.ordem}: ${de} → ${para}${mes ? ` a partir de ${rotuloOpcao(MESES_MAO_DE_OBRA, troca.mes)}` : " (mês não informado)"}. Encargos e benefícios passam a seguir o novo regime.`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700,
        background: `${C.warning}1F`, color: C.warning, border: `1px solid ${C.warning}55`, whiteSpace: "nowrap",
      }}
    >
      <ArrowLeftRight size={11} />
      {de} → {para}{mes ? ` · ${mes}` : ""}
    </span>
  );
}

function ComposicaoCusto({ C, colaborador: c }) {
  return (
    <div style={{ display: "flex", gap: 32, flexWrap: "wrap", fontSize: 12 }}>
      <div>
        <p style={{ margin: "0 0 6px", fontWeight: 700, color: C.muted, textTransform: "uppercase", fontSize: 10, letterSpacing: "0.06em" }}>Salário (dez/{c.ano_referencia})</p>
        <p style={{ margin: 0, fontWeight: 700, color: C.text }}>{formatCurrency(c.salario_bruto)}</p>
      </div>
      <div>
        <p style={{ margin: "0 0 6px", fontWeight: 700, color: C.muted, textTransform: "uppercase", fontSize: 10, letterSpacing: "0.06em" }}>Encargos</p>
        {c.composicao_encargos.length === 0 ? (
          <p style={{ margin: 0, color: C.muted }}>Sem encargos no mês</p>
        ) : c.composicao_encargos.map((item) => (
          <p key={item.codigo} style={{ margin: "0 0 2px", color: C.text }}>
            {item.label} ({formatPercent(item.percentual * 100)}): <strong>{formatCurrency(item.valor)}</strong>
            {item.incluir_no_total === false && <span style={{ color: C.muted, fontStyle: "italic" }}> · fora do total</span>}
          </p>
        ))}
      </div>
      <div>
        <p style={{ margin: "0 0 6px", fontWeight: 700, color: C.muted, textTransform: "uppercase", fontSize: 10, letterSpacing: "0.06em" }}>Benefícios</p>
        {c.composicao_beneficios.length === 0 ? (
          <p style={{ margin: 0, color: C.muted }}>Nenhum benefício aplicável</p>
        ) : c.composicao_beneficios.map((item) => (
          <p key={item.codigo} style={{ margin: "0 0 2px", color: C.text }}>{item.label}: <strong>{formatCurrency(item.valor)}</strong></p>
        ))}
      </div>
    </div>
  );
}

// ── Modal: Editar detalhes do colaborador ────────────────────────────────────
// Reúne tudo que saiu da linha da tabela: classificação, os 2 reajustes, vigência,
// distribuição mensal calculada e composição de custo. Ao contrário das células da tabela
// (que salvam sozinhas no onBlur), os campos aqui só ficam em um rascunho local — o modal
// costuma ser usado para simular cenários, e salvar a cada tecla apagaria o dado real de uma
// simulação que o usuário decidiu não aplicar. Só grava no backend ao clicar "Salvar alterações".

function ModalEditarColaborador({ C, colaborador: c, novo, departamentos, visaoAtual, ano, cargosExistentes, onSalvar, onClose }) {
  const [draft, setDraft] = useState(() => clonarColaborador(c));
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");

  // Tabela salarial do ano (já corrigida pelo índice quando herdada) — só para mostrar, a título
  // de referência, o salário que corresponde ao Agrupamento × Enquadramento escolhidos no modal.
  // O salário real continua sendo calculado/gravado pelo backend.
  const [tabelaSalarial, setTabelaSalarial] = useState(null);
  useEffect(() => {
    let ativo = true;
    getTabelaSalarialMaoDeObra(ano)
      .then((t) => { if (ativo) setTabelaSalarial(t); })
      .catch(() => { if (ativo) setTabelaSalarial(null); });
    return () => { ativo = false; };
  }, [ano]);
  // Itens = tabela CLT; PJ = CLT × (1 + adicional_pj%) — mesma regra do backend.
  function salarioDaTabela(agrupamento, enquadramento, regime) {
    if (!tabelaSalarial || agrupamento == null || enquadramento == null) return null;
    const item = tabelaSalarial.itens.find((i) => Number(i.agrupamento) === Number(agrupamento)
      && Math.abs(Number(i.enquadramento) - Number(enquadramento)) < 0.001);
    return item ? Number(item.valor_referencia) * fatorRegime(tabelaSalarial, regime) : null;
  }
  const salarioTabela = salarioDaTabela(draft.agrupamento, draft.enquadramento, draft.regime_atual);

  // Catálogo de Cargos do RH (cargo → agrupamento) — no Reajuste, o cargo define o agrupamento.
  const [catalogoCargos, setCatalogoCargos] = useState([]);
  useEffect(() => {
    let ativo = true;
    getCargosMaoDeObra()
      .then((lista) => { if (ativo) setCatalogoCargos(lista); })
      .catch(() => { if (ativo) setCatalogoCargos([]); });
    return () => { ativo = false; };
  }, []);

  // Nova contratação — mesma lógica do Reajuste: o cargo do catálogo define o agrupamento e o
  // salário sai da faixa (80%–120%) desse agrupamento; fora do catálogo o agrupamento é manual.
  const porCargoCatalogo = useMemo(() => new Map(catalogoCargos.map((item) => [chaveCargo(item.cargo), item])), [catalogoCargos]);
  const itemCatalogoNovo = novo ? porCargoCatalogo.get(chaveCargo(draft.cargo)) : null;
  const sugestoesCargo = useMemo(() => {
    const doCatalogo = catalogoCargos.map((item) => ({ cargo: item.cargo, rotulo: `Agrupamento ${item.agrupamento}` }));
    const foraDoCatalogo = (cargosExistentes || [])
      .filter((cargo) => !porCargoCatalogo.has(chaveCargo(cargo)))
      .map((cargo) => ({ cargo, rotulo: "Fora do catálogo" }));
    return [...doCatalogo, ...foraDoCatalogo].sort((x, y) => x.cargo.localeCompare(y.cargo, "pt-BR"));
  }, [catalogoCargos, cargosExistentes, porCargoCatalogo]);
  function trocarCargo(valor) {
    const item = porCargoCatalogo.get(chaveCargo(valor));
    setDraft((d) => ({ ...d, cargo: valor, ...(item ? { agrupamento: item.agrupamento } : {}) }));
  }

  const r1 = reajusteAtual(draft, 1);
  const r2 = reajusteAtual(draft, 2);
  // Situação de partida de cada reajuste: o R1 parte do cadastro; o R2 parte do R1 (se ativo).
  const atualR1 = {
    excecao: !!draft.salario_excecao,
    cargo: draft.cargo, agrupamento: draft.agrupamento, enquadramento: draft.enquadramento, regime: draft.regime_atual,
    salario: draft.salario_excecao ? Number(draft.salario_bruto_atual || 0) : (salarioDaTabela(draft.agrupamento, draft.enquadramento, draft.regime_atual) ?? Number(draft.salario_bruto_atual || 0)),
  };
  const regimeR1 = r1.regime || draft.regime_atual;
  const atualR2 = r1.ativo ? {
    excecao: !!r1.salario_excecao,
    cargo: r1.cargo || draft.cargo,
    agrupamento: r1.agrupamento ?? draft.agrupamento,
    enquadramento: r1.enquadramento ?? draft.enquadramento,
    regime: regimeR1,
    salario: r1.salario_excecao ? Number(r1.salario || 0) : (salarioDaTabela(r1.agrupamento, r1.enquadramento, regimeR1) ?? Number(r1.salario || 0)),
  } : atualR1;
  const meses = c[visaoAtual.campoMensal] || {};
  const alterado = JSON.stringify(draft) !== JSON.stringify(clonarColaborador(c));
  // Veio de um ano anterior: a edição vale a partir de janeiro deste ano, e início de vigência /
  // contratação nova pertencem ao ano de entrada (o backend recusa alterá-los aqui).
  const herdado = !novo && c.ano_entrada < c.ano_referencia;

  // Nova contratação marcada como "contratação nova": depois dos dados do contratante vêm as
  // etapas Kit Admissão e Assinaturas e Sistemas (mesmos componentes das modais Lançar Kit
  // Admissão); só o "Criar contratação" da última etapa grava tudo. OPERAÇÃO lança o kit em
  // Rateio Operações e as assinaturas opcionais são rateadas pelos projetos da Alocação de
  // Pessoal do colaborador (sem escolher projeto aqui); os demais departamentos lançam em
  // Despesas Gerais do próprio departamento. A Google vai sempre para a Diretoria
  // Administrativo Financeiro (regra do backend).
  // Na edição (ano de entrada) as mesmas etapas mostram o kit vinculado à contratação: dá para
  // trocar perfil/assinaturas/projetos ou remover; mudar o mês de entrada leva o kit junto.
  const comKit = !!draft.nova_contratacao && (novo || !herdado);
  const ehOperacao = draft.departamento === DEPARTAMENTO_OPERACAO;
  const [etapa, setEtapa] = useState(0);
  const [tipoKit, setTipoKit] = useState("atendimento");
  const [assinaturas, setAssinaturas] = useState([]);
  const [equipamentos, setEquipamentos] = useState(EQUIPAMENTOS_KIT_ADMISSAO_PADRAO);
  // Edição: kit vinculado como veio do backend (null = sem kit) e se ele fica/entra.
  const [kitOriginal, setKitOriginal] = useState(null);
  const [kitCarregado, setKitCarregado] = useState(novo);
  const [incluirKit, setIncluirKit] = useState(true);
  useEffect(() => {
    if (novo || !comKit || kitCarregado) return;
    getKitAdmissaoColaborador(c.id)
      .then(({ kit }) => {
        if (kit) {
          setTipoKit(kit.tipo_kit);
          setAssinaturas(kit.assinaturas);
          setEquipamentos(kit.equipamentos);
        }
        setKitOriginal(kit);
        setIncluirKit(!!kit);
      })
      .catch(() => setIncluirKit(false))
      .finally(() => setKitCarregado(true));
  }, [novo, comKit, kitCarregado, c.id]);
  const [ticketsKit, setTicketsKit] = useState([]);
  useEffect(() => {
    if (!comKit) return;
    Promise.all([getEquipamentosTicketMedio(ano), getAssinaturasTicketMedio(ano)])
      .then(([equipamentos, assinaturasTicket]) => setTicketsKit([...equipamentos, ...assinaturasTicket]))
      .catch(() => {});
  }, [comKit, ano]);
  const ticketPorCodigo = useMemo(() => Object.fromEntries(ticketsKit.map((t) => [t.codigo, t.ticket_medio])), [ticketsKit]);
  const equipPorCodigo = useMemo(() => Object.fromEntries(ticketsKit.map((t) => [t.codigo, t])), [ticketsKit]);
  const mesKit = Number(draft.vigencia_inicio) || 1;
  const custoEquipamentos = custoEquipamentosKit(tipoKit, equipamentos, ticketPorCodigo);
  const custoAssinaturas = estimativaAssinaturasKit(assinaturas, ticketPorCodigo, 1, mesKit);
  // Etapas da modal: Cadastro → Reajuste → (Kit Admissão → Assinaturas, se contratação nova
  // com kit) → Resumo (só edição). Na edição as etapas só organizam a tela: navegação livre e
  // "Salvar alterações" em qualquer etapa. Na criação é um assistente: avança até "Criar contratação".
  const passos = [
    { id: "cadastro", label: novo ? "Dados do contratante" : "Cadastro" },
    { id: "reajuste", label: "Reajuste" },
    ...(comKit ? [{ id: "kit", label: "Kit Admissão" }, ...(incluirKit ? [{ id: "assinaturas", label: "Assinaturas e Sistemas" }] : [])] : []),
    ...(novo ? [] : [{ id: "resumo", label: "Resumo" }]),
  ];
  const ultimaEtapa = passos.length - 1;
  const etapaAtual = Math.min(etapa, ultimaEtapa);
  const passoAtual = passos[etapaAtual].id;
  const irPara = (id) => { setErro(""); setEtapa(passos.findIndex((p) => p.id === id)); };

  function kitAdmissaoPayload() {
    if (!comKit || !incluirKit) return null;
    return {
      tipo_kit: tipoKit, mes: mesKit,
      item_nome: draft.cargo?.trim() ? `Admissão ${draft.cargo.trim()}` : null,
      assinaturas, equipamentos,
    };
  }

  // Kit mudou em relação ao vinculado? (só edição; o nome acompanha o cargo e não conta sozinho)
  const semNome = (k) => (k ? {
    tipo_kit: k.equipamentos.includes("NOTEBOOK") ? k.tipo_kit : null, mes: k.mes,
    assinaturas: [...k.assinaturas].sort(), equipamentos: [...k.equipamentos].sort(),
  } : null);
  const kitAlterado = !novo && comKit && kitCarregado
    && JSON.stringify(semNome(kitAdmissaoPayload())) !== JSON.stringify(semNome(kitOriginal));

  function avancar() {
    setErro("");
    setEtapa(Math.min(etapaAtual + 1, ultimaEtapa));
  }
  const comReajuste = !!(r1.ativo || r2.ativo);
  const [avancadoAberto, setAvancadoAberto] = useState({});
  const [composicaoAberta, setComposicaoAberta] = useState(false);

  function atualizarCampo(campo, valor) {
    setDraft((d) => ({ ...d, [campo]: valor }));
  }
  // Reajuste ativo e "sem reajuste no ano" são excludentes — marcar um desmarca o outro.
  function atualizarReajuste(ordem, patch) {
    setDraft((d) => ({
      ...d,
      [`reajuste_${ordem}`]: { ...reajusteAtual(d, ordem), ...patch },
      ...(patch.ativo ? { sem_reajuste: false } : {}),
    }));
  }
  function marcarSemReajuste(valor) {
    setDraft((d) => (valor
      ? { ...d, sem_reajuste: true, reajuste_1: { ...reajusteAtual(d, 1), ativo: false }, reajuste_2: { ...reajusteAtual(d, 2), ativo: false } }
      : { ...d, sem_reajuste: false }));
  }

  function tentarFechar() {
    if (alterado && !window.confirm("Existem alterações não salvas neste colaborador. Deseja descartá-las?")) return;
    onClose();
  }

  async function salvar() {
    // Reajuste ativo pela tabela precisa de agrupamento (do cargo ou manual) e enquadramento —
    // sem eles não há célula na tabela salarial e o salário zeraria.
    for (const [ordem, r, atual] of [[1, r1, atualR1], [2, r2, atualR2]]) {
      if (!r.ativo || r.salario_excecao) continue;
      const doCatalogo = catalogoCargos.find((item) => chaveCargo(item.cargo) === chaveCargo(r.cargo || atual.cargo));
      if ((doCatalogo ? doCatalogo.agrupamento : r.agrupamento) == null || r.enquadramento == null) {
        irPara("reajuste");
        setErro(`Reajuste ${ordem}: escolha o enquadramento na faixa${doCatalogo || r.agrupamento != null ? "" : " e o agrupamento"} — sem isso o salário ficaria zerado.`);
        return;
      }
    }
    setSalvando(true);
    setErro("");
    try {
      await onSalvar(draft, kitAdmissaoPayload(), kitAlterado);
      onClose();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  const resumoColunas = [
    { titulo: "Janeiro", ...atualR1 },
    ...(r1.ativo ? [{ titulo: `Reajuste 1${r1.mes ? ` · ${MESES_ABREV[r1.mes - 1]}` : ""}`, ...resultadoReajuste(r1, atualR1) }] : []),
    ...(r2.ativo ? [{ titulo: `Reajuste 2${r2.mes ? ` · ${MESES_ABREV[r2.mes - 1]}` : ""}`, ...resultadoReajuste(r2, atualR2) }] : []),
  ];
  function resultadoReajuste(r, atual) {
    const regime = r.regime || draft.regime_atual;
    const item = catalogoCargos.find((i) => chaveCargo(i.cargo) === chaveCargo(r.cargo || atual.cargo));
    const agrupamento = item ? item.agrupamento : r.agrupamento;
    const salario = r.salario_excecao ? Number(r.salario || 0) : salarioDaTabela(agrupamento, r.enquadramento, regime);
    return { excecao: !!r.salario_excecao, cargo: r.cargo || atual.cargo, agrupamento, enquadramento: r.enquadramento, regime, salario };
  }
  // Gráfico do Resumo: os valores gravados (c) só refletem o rascunho depois de salvar. Na visão
  // Salário Bruto com alterações (ou nova contratação, que ainda não tem nada gravado), projeta o
  // salário do rascunho mês a mês — salário de janeiro, troca no mês de cada reajuste ativo e zero
  // fora da vigência (mesma regra de salario_no_mes no backend, sem o índice dos anos seguintes).
  const projetarRascunho = visaoAtual.id === "salario" && (novo || alterado);
  const mesesGrafico = projetarRascunho ? (() => {
    const inicio = herdado ? 1 : Number(draft.vigencia_inicio) || 1;
    const fim = Number(draft.vigencia_fim) || 12;
    // resumoColunas = [janeiro, R1 (se ativo), R2 (se ativo)]
    const colunaR1 = r1.ativo ? resumoColunas[1] : null;
    const colunaR2 = r2.ativo ? resumoColunas[r1.ativo ? 2 : 1] : null;
    const tiers = [
      { mes: 1, salario: resumoColunas[0].salario },
      ...(colunaR1 && r1.mes ? [{ mes: Number(r1.mes), salario: colunaR1.salario }] : []),
      ...(colunaR2 && r2.mes ? [{ mes: Number(r2.mes), salario: colunaR2.salario }] : []),
    ].sort((x, y) => x.mes - y.mes);
    return Object.fromEntries(MESES_ABREV.map((_, i) => {
      const mes = i + 1;
      if (mes < inicio || mes > fim) return [mes, 0];
      const tier = tiers.filter((t) => t.mes <= mes).pop();
      return [mes, Number(tier?.salario || 0)];
    }));
  })() : meses;
  const totalGrafico = projetarRascunho
    ? MESES_ABREV.reduce((acc, _, i) => acc + Number(mesesGrafico[i + 1] || 0), 0)
    : Number(c[visaoAtual.campoTotal] || 0);
  const salarioDezGrafico = projetarRascunho ? Number(mesesGrafico[12] || 0) : c.salario_bruto;
  const maiorMes = Math.max(1, ...MESES_ABREV.map((_, i) => Number(mesesGrafico[i + 1] || 0)));
  const podeSalvar = !salvando && (alterado || kitAlterado);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, width: "100%", maxWidth: 820, boxShadow: "0 24px 64px rgba(0,0,0,0.4)", height: "min(90vh, 860px)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Cabeçalho fixo: quem é + etapas */}
        <div style={{ padding: "20px 24px 0", borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>{(novo ? draft.cargo : c.cargo) || "Cargo não informado"}</p>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: C.muted }}>{c.ocupante || "Vaga em aberto"} · {c.departamento} · {ano}</p>
            </div>
            <button onClick={tentarFechar} aria-label="Fechar" style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, display: "flex", padding: 4 }}>
              <X size={20} />
            </button>
          </div>
          <div style={{ display: "flex", gap: 2, marginTop: 10, overflowX: "auto" }}>
            {passos.map((p, i) => {
              const ativo = i === etapaAtual;
              // Criação é assistente: só volta para etapas já vistas. Edição: navegação livre.
              const clicavel = !novo || i < etapaAtual;
              return (
                <button key={p.id} type="button" disabled={!clicavel && !ativo} onClick={() => { setErro(""); setEtapa(i); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", background: "none", border: "none",
                    borderBottom: `3px solid ${ativo ? C.brand : "transparent"}`, fontFamily: "inherit", fontSize: 13,
                    fontWeight: ativo ? 700 : 600, color: ativo ? C.brand : C.muted, whiteSpace: "nowrap",
                    cursor: clicavel ? "pointer" : "default",
                  }}>
                  <span style={{
                    width: 22, height: 22, borderRadius: 11, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center",
                    background: ativo ? C.brand : "transparent", color: ativo ? "#fff" : C.muted, border: ativo ? "none" : `1.5px solid ${C.border}`,
                  }}>{i + 1}</span>
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Corpo rolável */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
          {passoAtual === "cadastro" && (<>
          {herdado && (
            <p style={{ margin: 0, fontSize: 11, color: C.muted, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 10px" }}>
              Colaborador ativo desde {c.ano_entrada}: o que for alterado aqui vale a partir de janeiro de {c.ano_referencia}, sem mudar os anos anteriores.
            </p>
          )}

          <ModalSecao C={C} titulo="Classificação">
            {novo && (
              <div style={{ marginBottom: 12 }}>
                <CampoModal C={C} label="Cargo/Função (escolha do catálogo de cargos ou digite um novo)">
                  <input type="text" autoFocus placeholder="Ex: Analista Financeiro" value={draft.cargo || ""} list="mao-de-obra-cargos"
                    onChange={(e) => trocarCargo(e.target.value)} style={celulaInputStyle(C)} />
                  <datalist id="mao-de-obra-cargos">
                    {sugestoesCargo.map((item) => <option key={item.cargo} value={item.cargo}>{item.rotulo}</option>)}
                  </datalist>
                </CampoModal>
                {draft.cargo?.trim() && (itemCatalogoNovo ? (
                  <p style={{ margin: "6px 0 0", fontSize: 11.5, color: C.muted }}>
                    Agrupamento <strong style={{ color: C.brand }}>{itemCatalogoNovo.agrupamento}</strong> definido pelo cargo no catálogo do RH — escolha o enquadramento na faixa abaixo.
                  </p>
                ) : catalogoCargos.length > 0 && (
                  <p style={{ margin: "6px 0 0", fontSize: 11.5, color: "#B45309" }}>
                    "{draft.cargo.trim()}" não está no catálogo de cargos do RH — informe o agrupamento abaixo.
                  </p>
                ))}
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12 }}>
              <CampoModal C={C} label="Departamento">
                <CampoSelect C={C} valor={draft.departamento} onChange={(v) => atualizarCampo("departamento", v)} opcoes={departamentos.map((d) => ({ value: d, label: d }))} />
              </CampoModal>
              <CampoModal C={C} label="Nível do cargo">
                <CampoSelect C={C} valor={draft.nivel_cargo} onChange={(v) => atualizarCampo("nivel_cargo", v)} opcoes={NIVEIS_CARGO} />
              </CampoModal>
              <CampoModal C={C} label="Modelo de trabalho">
                <CampoSelect C={C} valor={draft.modelo_trabalho} onChange={(v) => atualizarCampo("modelo_trabalho", v)} opcoes={MODELOS_TRABALHO} />
              </CampoModal>
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.text, cursor: "pointer", marginTop: 12 }}>
              <input type="checkbox" checked={!!draft.plano_previdencia} onChange={(e) => atualizarCampo("plano_previdencia", e.target.checked)} style={{ cursor: "pointer" }} />
              Possui plano de previdência
            </label>
          </ModalSecao>

          <ModalSecao C={C} titulo={`Salário ${novo ? `na contratação` : "em janeiro"} · tabela ${ano}`}>
            {novo ? (
              <SalarioNovaContratacao C={C} draft={draft} itemCatalogo={itemCatalogoNovo} tabelaSalarial={tabelaSalarial}
                salarioTabela={salarioTabela} salarioDaTabela={salarioDaTabela}
                onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))} />
            ) : (<>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12, alignItems: "end" }}>
              <CampoModal C={C} label="Regime">
                <CampoSelect C={C} valor={draft.regime_atual} onChange={(v) => atualizarCampo("regime_atual", v)} opcoes={REGIMES_MAO_DE_OBRA} />
              </CampoModal>
              <CampoModal C={C} label="Agrupamento">
                <CampoSelectAgrupamento C={C} valor={draft.agrupamento} onChange={(v) => atualizarCampo("agrupamento", v)} />
              </CampoModal>
              <CampoModal C={C} label="Enquadramento">
                <CampoSelectEnquadramento C={C} valor={draft.enquadramento} onChange={(v) => atualizarCampo("enquadramento", v)} />
              </CampoModal>
              {/* Salário fora da tabela (desenquadramento — caso da maioria vinda da carga): o real
                  é o salario_bruto_atual (já com índice); agrupamento × enquadramento não são usados. */}
              {draft.salario_excecao ? (
                <div title="Salário real do colaborador (fora da tabela salarial)"
                  style={{ background: `${C.brand}12`, borderRadius: 8, padding: "6px 12px", textAlign: "right" }}>
                  <span style={{ display: "block", fontSize: 11, color: C.muted, fontWeight: 600 }}>Salário atual · fora da tabela</span>
                  <span style={{ fontSize: 16, fontWeight: 800, color: C.text }}>{formatCurrency(Number(draft.salario_bruto_atual || 0))}</span>
                </div>
              ) : (
                <div title="Valor da tabela salarial para o Agrupamento × Enquadramento escolhidos"
                  style={{ background: `${C.brand}12`, borderRadius: 8, padding: "6px 12px", textAlign: "right" }}>
                  <span style={{ display: "block", fontSize: 11, color: C.muted, fontWeight: 600 }}>
                    Salário{draft.regime_atual === "PJ" && tabelaSalarial ? ` · PJ +${formatarPercentualPj(tabelaSalarial.adicional_pj)}` : ""}
                  </span>
                  <span style={{ fontSize: 16, fontWeight: 800, color: salarioTabela != null ? C.text : C.muted }}>
                    {salarioTabela != null ? formatCurrency(salarioTabela) : "—"}
                  </span>
                </div>
              )}
            </div>
            {!novo && draft.salario_excecao && (
              <p style={{ margin: "8px 0 0", fontSize: 11, color: C.muted }}>
                Ainda não enquadrado na tabela salarial: o salário vale o valor real acima, e Agrupamento × Enquadramento não são usados no cálculo
                {salarioTabela != null ? ` (pela tabela, Agrup. ${rotuloAgrupamento(draft.agrupamento)} · ${rotuloEnquadramento(draft.enquadramento)} daria ${formatCurrency(salarioTabela)})` : ""}.
                {" "}O enquadramento acontece no Reajuste, ao escolher o cargo e a faixa.
              </p>
            )}
            </>)}
          </ModalSecao>

          <ModalSecao C={C} titulo="Vigência">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <CampoModal C={C} label={herdado ? `Vigência Início (ativo desde ${c.ano_entrada})` : "Vigência Início"}>
                <CampoSelect C={C} valor={draft.vigencia_inicio} onChange={(v) => atualizarCampo("vigencia_inicio", Number(v))} opcoes={MESES_MAO_DE_OBRA} disabled={herdado} />
              </CampoModal>
              <CampoModal C={C} label="Vigência Fim">
                <CampoSelect C={C} valor={draft.vigencia_fim} onChange={(v) => atualizarCampo("vigencia_fim", Number(v))} opcoes={MESES_MAO_DE_OBRA} />
              </CampoModal>
            </div>
            {!herdado && (
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.text, cursor: "pointer", marginTop: 10 }}>
                <input type="checkbox" checked={!!draft.nova_contratacao} onChange={(e) => atualizarCampo("nova_contratacao", e.target.checked)} style={{ cursor: "pointer" }} />
                É uma contratação nova (conta em Vigência Início na Auditoria de Kits de Admissão)
              </label>
            )}
            {draft.nova_contratacao && (
              <p style={{ margin: "6px 0 0", fontSize: 11, color: C.muted }}>
                Confirme que "Vigência Início" acima é o mês real da contratação — não deixe no padrão (Janeiro) se a pessoa começou em outro mês.
              </p>
            )}
          </ModalSecao>

          </>)}

          {passoAtual === "reajuste" && (<>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
            {[
              { id: "sem", titulo: `Sem reajuste em ${c.ano_referencia || ano}`, texto: "Mantém o salário o ano todo", ativo: !!draft.sem_reajuste, onClick: () => marcarSemReajuste(true) },
              { id: "com", titulo: "Com reajuste", texto: "Promoção, mudança de faixa ou de regime", ativo: comReajuste, onClick: () => { if (!comReajuste) atualizarReajuste(1, { ativo: true }); } },
            ].map((op) => (
              <button key={op.id} type="button" onClick={op.onClick} style={{
                textAlign: "left", padding: "12px 14px", borderRadius: 10, cursor: "pointer", fontFamily: "inherit",
                border: op.ativo ? `2px solid ${C.brand}` : `1px solid ${C.border}`, background: op.ativo ? `${C.brand}12` : C.card,
              }}>
                <strong style={{ display: "block", fontSize: 13, color: C.text }}>{op.titulo}</strong>
                <span style={{ fontSize: 11.5, color: C.muted }}>{op.texto}</span>
              </button>
            ))}
          </div>
          {!draft.sem_reajuste && !comReajuste && (
            <p style={{ margin: 0, fontSize: 12, color: C.muted }}>Escolha uma das opções — as duas contam como reajuste inserido no Progresso Orçamentário.</p>
          )}
          {draft.sem_reajuste && (
            <p style={{ margin: 0, fontSize: 12, color: C.muted, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px" }}>
              Conta como reajuste inserido no Progresso Orçamentário. O salário de janeiro segue até dezembro.
            </p>
          )}
          {comReajuste && (<>
            {r1.ativo ? (
              <ModalSecaoReajuste C={C} titulo="Reajuste 1" ordem={1} reajuste={r1} rotuloMes="A partir de"
                atual={atualR1} regimePadrao={draft.regime_atual} catalogoCargos={catalogoCargos} salarioDaTabela={salarioDaTabela}
                avancadoAberto={!!avancadoAberto[1] || !!r1.salario_excecao} onAlternarAvancado={() => setAvancadoAberto((a) => ({ ...a, 1: !a[1] }))}
                onChange={(patch) => atualizarReajuste(1, patch)}
                onRemover={r2.ativo ? () => atualizarReajuste(1, { ativo: false }) : null}
              />
            ) : (
              <button type="button" onClick={() => atualizarReajuste(1, { ativo: true })} style={botaoAdicionarReajusteStyle(C)}>+ Ativar Reajuste 1</button>
            )}
            {r2.ativo ? (
              <ModalSecaoReajuste C={C} titulo="Reajuste 2" ordem={2} reajuste={r2} rotuloMes="A partir de"
                atual={atualR2} regimePadrao={draft.regime_atual} catalogoCargos={catalogoCargos} salarioDaTabela={salarioDaTabela}
                avancadoAberto={!!avancadoAberto[2] || !!r2.salario_excecao} onAlternarAvancado={() => setAvancadoAberto((a) => ({ ...a, 2: !a[2] }))}
                onChange={(patch) => atualizarReajuste(2, patch)}
                onRemover={() => atualizarReajuste(2, { ativo: false })}
              />
            ) : (
              <button type="button" onClick={() => atualizarReajuste(2, { ativo: true })} style={botaoAdicionarReajusteStyle(C)}>+ Adicionar 2º reajuste no ano</button>
            )}
          </>)}
          </>)}

          {passoAtual === "resumo" && (<>
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle(C, false, "left"), position: "static" }}>O que muda</th>
                  {resumoColunas.map((col) => <th key={col.titulo} style={{ ...thStyle(C, false, "left"), position: "static" }}>{col.titulo}</th>)}
                </tr>
              </thead>
              <tbody>
                {[
                  { l: "Cargo", v: (col) => col.cargo || "—" },
                  { l: "Faixa", v: (col) => (col.excecao ? "Fora da tabela" : `Agrup. ${rotuloAgrupamento(col.agrupamento)} · ${rotuloEnquadramento(col.enquadramento)}`) },
                  { l: "Regime", v: (col) => rotuloOpcao(REGIMES_MAO_DE_OBRA, col.regime) },
                  { l: "Salário", v: (col, i) => {
                    if (col.salario == null) return "—";
                    const base = resumoColunas[0].salario;
                    const variacao = i > 0 && base > 0 ? ` (${col.salario >= base ? "+" : ""}${formatPercent((col.salario / base - 1) * 100)})` : "";
                    return `${formatCurrency(col.salario)}${variacao}`;
                  } },
                ].map((linha) => (
                  <tr key={linha.l} style={{ borderTop: `1px solid ${C.border}` }}>
                    <td style={{ padding: "9px 12px", color: C.muted, fontWeight: 600 }}>{linha.l}</td>
                    {resumoColunas.map((col, i) => (
                      <td key={col.titulo} style={{ padding: "9px 12px", color: C.text, fontWeight: i > 0 ? 700 : 400 }}>{linha.v(col, i)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {draft.sem_reajuste && <p style={{ margin: 0, fontSize: 12, color: C.muted }}>Sem reajuste em {c.ano_referencia}.</p>}

          <ModalSecao C={C} titulo={`${visaoAtual.label} mês a mês (calculado)`}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(12, minmax(0, 1fr))", gap: 6, alignItems: "end", height: 130 }}>
              {MESES_ABREV.map((m, i) => {
                const v = Number(mesesGrafico[i + 1] || 0);
                return (
                  <div key={m} title={`${m}: ${formatCurrency(v)}`} style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", gap: 4, height: "100%" }}>
                    <span style={{ fontSize: 9, color: C.muted, fontWeight: 600 }}>{v ? `${(v / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}k` : "—"}</span>
                    <div style={{ width: "100%", height: Math.round((v / maiorMes) * 88), minHeight: v ? 2 : 0, borderRadius: "4px 4px 0 0", background: C.brand, opacity: 0.85 }} />
                    <span style={{ fontSize: 10, color: C.muted, fontWeight: 600 }}>{m}</span>
                  </div>
                );
              })}
            </div>
            <p style={{ margin: "10px 0 0", fontSize: 11, color: C.muted }}>
              Salário Bruto (Dez): <strong style={{ color: C.text }}>{formatCurrency(salarioDezGrafico)}</strong> · Total {visaoAtual.label}: <strong style={{ color: C.text }}>{formatCurrency(totalGrafico)}</strong>
              {projetarRascunho
                ? " · prévia com as alterações desta janela — o valor final é recalculado ao salvar"
                : alterado && " · valores gravados — recalculados após salvar"}
            </p>
          </ModalSecao>

          <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, padding: "0 16px" }}>
            <button type="button" onClick={() => setComposicaoAberta((v) => !v)} style={{
              width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", background: "none", border: "none",
              padding: "12px 0", fontFamily: "inherit", fontSize: 13, fontWeight: 700, color: C.text, cursor: "pointer",
            }}>
              <span>Composição de custo (encargos e benefícios)</span>
              <span style={{ color: C.brand, fontSize: 12 }}>{composicaoAberta ? "Ocultar" : "Ver"}</span>
            </button>
            {composicaoAberta && <div style={{ paddingBottom: 14 }}><ComposicaoCusto C={C} colaborador={c} /></div>}
          </div>
          </>)}

          {passoAtual === "kit" && (
            <ModalSecao C={C} titulo="Kit Admissão">
              {!novo && (
                <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12, color: C.text, marginBottom: 10, cursor: "pointer" }}>
                  <input type="checkbox" checked={incluirKit} onChange={(e) => setIncluirKit(e.target.checked)} style={{ marginTop: 2 }} />
                  <span>
                    {kitOriginal ? "Manter o Kit Admissão desta contratação" : "Lançar Kit Admissão para esta contratação"}
                    <span style={{ display: "block", color: C.muted, fontSize: 11, marginTop: 2 }}>
                      {kitOriginal
                        ? "Desmarcar remove o kit, as assinaturas e a Assinatura Google ao salvar."
                        : "Nenhum kit vinculado a esta contratação. Se ele já foi lançado à mão em Despesas Gerais / Custos Gerais por Projeto, não marque — o kit sairia em dobro."}
                    </span>
                  </span>
                </label>
              )}
              {(novo || incluirKit) && (<>
              <p style={{ margin: "0 0 10px", fontSize: 11, color: C.muted }}>
                Equipamentos marcados, comprados em {MESES_ABREV[mesKit - 1]} (Vigência Início), com os valores de Premissas · Equipamentos e Materiais.
                {" "}{ehOperacao
                  ? `Lançado em Custos Gerais por Projeto, projeto ${NOME_PROJETO_RATEIO_OPERACOES} (2.7.01).`
                  : `Lançado em Despesas Gerais de ${draft.departamento} (2.7.01).`}
              </p>
              <EquipamentosKit C={C} equipamentos={equipamentos} onChange={setEquipamentos} tipoKit={tipoKit} onTipoKit={setTipoKit} ticketPorCodigo={ticketPorCodigo} equipPorCodigo={equipPorCodigo} />
              {ticketsKit.length > 0 && (
                <p style={{ margin: "10px 0 0", fontSize: 12, color: C.text }}>
                  Equipamentos em {MESES_ABREV[mesKit - 1]}: <strong>{formatCurrency(custoEquipamentos)}</strong>
                </p>
              )}
              </>)}
            </ModalSecao>
          )}

          {passoAtual === "assinaturas" && (
            <ModalSecao C={C} titulo="Assinaturas e Sistemas">
              <p style={{ margin: "0 0 10px", fontSize: 11, color: C.muted }}>
                {ehOperacao
                  ? `Assinaturas opcionais lançadas em Custos Gerais por Projeto e divididas mês a mês pelos projetos em que a pessoa estiver alocada (Alocação de Pessoal); o que não estiver alocado fica em ${NOME_PROJETO_RATEIO_OPERACOES}.`
                  : `Assinaturas opcionais lançadas em Despesas Gerais de ${draft.departamento}.`}
              </p>
              <PerfilAssinaturasKit C={C} selecionadas={assinaturas} onChange={setAssinaturas} ticketPorCodigo={ticketPorCodigo} mes={mesKit} />
              {ticketsKit.length > 0 && (
                <p style={{ margin: "10px 0 0", fontSize: 12, color: C.text }}>
                  Assinaturas: <strong>{formatCurrency(custoAssinaturas.mensalPorUsuario)}</strong>/mês × {custoAssinaturas.meses} {custoAssinaturas.meses === 1 ? "mês" : "meses"} = <strong>{formatCurrency(custoAssinaturas.total)}</strong>
                </p>
              )}
            </ModalSecao>
          )}

          {erro && <p style={{ margin: 0, fontSize: 12, color: C.danger }}>{erro}</p>}
        </div>

        {/* Rodapé fixo */}
        <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "14px 24px", borderTop: `1px solid ${C.border}`, background: C.card }}>
          <span style={{ flex: 1, fontSize: 11, color: C.muted }}>
            {novo ? "Nada é gravado até criar a contratação." : "Nada é gravado até salvar."}
          </span>
          <button onClick={etapaAtual > 0 ? () => { setErro(""); setEtapa(etapaAtual - 1); } : tentarFechar} disabled={salvando} style={botaoRodapeStyle(C, "neutro", salvando)}>
            {etapaAtual > 0 ? "Voltar" : "Cancelar"}
          </button>
          {novo ? (
            <button onClick={etapaAtual < ultimaEtapa ? avancar : salvar} disabled={!podeSalvar} style={botaoRodapeStyle(C, "primario", !podeSalvar)}>
              {salvando ? "Salvando..." : etapaAtual < ultimaEtapa ? `Avançar: ${passos[etapaAtual + 1].label}` : "Criar contratação"}
            </button>
          ) : (<>
            {etapaAtual < ultimaEtapa && (
              <button onClick={avancar} disabled={salvando} style={botaoRodapeStyle(C, "secundario", salvando)}>
                Próximo: {passos[etapaAtual + 1].label}
              </button>
            )}
            <button onClick={salvar} disabled={!podeSalvar} style={botaoRodapeStyle(C, "primario", !podeSalvar)}>
              {salvando ? "Salvando..." : "Salvar alterações"}
            </button>
          </>)}
        </div>
      </div>
    </div>
  );
}

function ModalSecao({ C, titulo, children }) {
  return (
    <div>
      <p style={{ margin: "0 0 8px", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>{titulo}</p>
      {children}
    </div>
  );
}

function CampoModal({ C, label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 10, fontWeight: 700, color: C.muted }}>{label}</label>
      <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, background: C.bg }}>{children}</div>
    </div>
  );
}

function ModalSecaoReajuste({ C, titulo, ordem, reajuste, rotuloMes, atual, regimePadrao, catalogoCargos, salarioDaTabela, avancadoAberto, onAlternarAvancado, onChange, onRemover }) {
  const porCargo = useMemo(() => new Map(catalogoCargos.map((item) => [chaveCargo(item.cargo), item])), [catalogoCargos]);
  // Cargo vazio = mantém o cargo atual. Cargo do catálogo define o agrupamento (o backend aplica
  // a mesma regra ao salvar); fora do catálogo (ex.: Diretor, Estágio) o agrupamento é manual.
  const cargoEfetivo = reajuste.cargo || atual.cargo;
  const itemCatalogo = porCargo.get(chaveCargo(cargoEfetivo));
  const agrupamento = itemCatalogo ? itemCatalogo.agrupamento : reajuste.agrupamento;
  const regime = reajuste.regime || regimePadrao;
  const salarioTabela = salarioDaTabela(agrupamento, reajuste.enquadramento, regime);
  const salarioNovo = reajuste.salario_excecao ? Number(reajuste.salario || 0) : salarioTabela;
  const variacao = (v) => (atual.salario > 0 && v != null ? (v / atual.salario - 1) * 100 : null);
  const variacaoNovo = variacao(salarioNovo);
  const rotuloNum = (n) => <strong style={{ color: C.brand }}>{n}.</strong>;

  function trocarCargo(valor) {
    const item = porCargo.get(chaveCargo(valor || atual.cargo));
    onChange({ cargo: valor || null, ...(item ? { agrupamento: item.agrupamento } : {}) });
  }

  return (
    <section style={{ border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.text }}>{titulo}</p>
        {onRemover && (
          <button type="button" onClick={onRemover} style={{ background: "none", border: "none", fontFamily: "inherit", fontSize: 12, fontWeight: 600, color: C.danger, cursor: "pointer" }}>Remover</button>
        )}
      </div>

      {/* Antes → Depois */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 28px minmax(0, 1fr)", gap: 8, alignItems: "stretch" }}>
        <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 12px" }}>
          <span style={{ fontSize: 10, fontWeight: 800, color: C.muted, letterSpacing: "0.06em" }}>{ordem === 2 ? "APÓS O REAJUSTE 1" : "HOJE"}</span>
          <p style={{ margin: "2px 0", fontSize: 12.5, fontWeight: 700, color: C.text }}>{atual.cargo || "Cargo não informado"}</p>
          <p style={{ margin: 0, fontSize: 11.5, color: C.muted }}>
            {atual.excecao ? "Fora da tabela" : `Agrup. ${rotuloAgrupamento(atual.agrupamento)} · ${rotuloEnquadramento(atual.enquadramento)}`} · {rotuloOpcao(REGIMES_MAO_DE_OBRA, atual.regime)}
          </p>
          <p style={{ margin: "4px 0 0", fontSize: 15, fontWeight: 800, color: C.text }}>{formatCurrency(atual.salario)}</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", color: C.brand }}>
          <ArrowRight size={20} aria-hidden="true" />
        </div>
        <div style={{ background: `${C.brand}12`, border: `1px solid ${C.brand}55`, borderRadius: 10, padding: "10px 12px" }}>
          <span style={{ fontSize: 10, fontWeight: 800, color: C.brand, letterSpacing: "0.06em" }}>
            {reajuste.mes ? `A PARTIR DE ${MESES_MAO_DE_OBRA.find((m) => Number(m.value) === Number(reajuste.mes))?.label?.toUpperCase() || ""}` : "A PARTIR DE —"}
          </span>
          <p style={{ margin: "2px 0", fontSize: 12.5, fontWeight: 700, color: C.text }}>{cargoEfetivo || "Cargo não informado"}</p>
          <p style={{ margin: 0, fontSize: 11.5, color: C.muted }}>Agrup. {rotuloAgrupamento(agrupamento)} · {rotuloEnquadramento(reajuste.enquadramento)} · {rotuloOpcao(REGIMES_MAO_DE_OBRA, regime)}</p>
          <p style={{ margin: "4px 0 0", fontSize: 15, fontWeight: 800, color: C.text }}>
            {salarioNovo != null ? formatCurrency(salarioNovo) : "—"}
            {variacaoNovo != null && (
              <span style={{ marginLeft: 6, fontSize: 12, fontWeight: 700, color: variacaoNovo >= 0 ? C.success : C.danger }}>
                {variacaoNovo >= 0 ? "+" : ""}{formatPercent(variacaoNovo)}
              </span>
            )}
          </p>
        </div>
      </div>

      {/* 1. cargo · 2. mês · regime */}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12, alignItems: "end" }}>
        <CampoModal C={C} label={<span>{rotuloNum(1)} Novo cargo (vazio = mantém o atual)</span>}>
          <input type="text" list={`mao-de-obra-cargos-reajuste-${ordem}`} value={reajuste.cargo || ""} placeholder={atual.cargo || "Escolha o cargo"}
            onChange={(e) => trocarCargo(e.target.value)} style={celulaInputStyle(C)} />
          <datalist id={`mao-de-obra-cargos-reajuste-${ordem}`}>
            {catalogoCargos.map((item) => <option key={item.id} value={item.cargo}>{`Agrupamento ${item.agrupamento}`}</option>)}
          </datalist>
        </CampoModal>
        <CampoModal C={C} label={<span>{rotuloNum(2)} {rotuloMes}</span>}>
          <CampoSelect C={C} valor={reajuste.mes || ""} onChange={(v) => onChange({ mes: v ? Number(v) : null })} opcoes={MESES_MAO_DE_OBRA} permiteVazio />
        </CampoModal>
        <CampoModal C={C} label="Regime">
          <CampoSelect C={C} valor={reajuste.regime || ""} onChange={(v) => onChange({ regime: v || null })}
            opcoes={[{ value: "", label: `Mantém (${rotuloOpcao(REGIMES_MAO_DE_OBRA, regimePadrao)})` }, ...REGIMES_MAO_DE_OBRA]} />
        </CampoModal>
      </div>
      {itemCatalogo ? (
        <p style={{ margin: "-6px 0 0", fontSize: 11.5, color: C.muted }}>
          Agrupamento <strong style={{ color: C.brand }}>{itemCatalogo.agrupamento}</strong> definido pelo cargo no catálogo do RH.
        </p>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: -6 }}>
          {cargoEfetivo && catalogoCargos.length > 0 && (
            <span style={{ fontSize: 11.5, color: "#B45309" }}>"{cargoEfetivo}" não está no catálogo de cargos do RH — informe o agrupamento:</span>
          )}
          <div style={{ width: 110, border: `1px solid ${C.border}`, borderRadius: 8, background: C.bg }}>
            <CampoSelectAgrupamento C={C} valor={reajuste.agrupamento} onChange={(v) => onChange({ agrupamento: v })} permiteVazio />
          </div>
        </div>
      )}

      {/* 3. faixa */}
      {reajuste.salario_excecao ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: C.text }}>
          Salário fora da tabela:
          <span style={{ width: 160 }}><CampoNumero C={C} valor={reajuste.salario} onSalvar={(v) => onChange({ salario: v })} /></span>
        </div>
      ) : (<>
        <FaixaEnquadramento C={C} agrupamento={agrupamento} regime={regime} selecionado={reajuste.enquadramento}
          atual={atual} salarioDaTabela={salarioDaTabela} variacao={variacao} rotulo={rotuloNum(3)}
          onEscolher={(enq) => onChange({ enquadramento: enq })} />
        {reajuste.enquadramento == null && agrupamento != null && (
          <p style={{ margin: "-6px 0 0", fontSize: 11.5, fontWeight: 700, color: C.danger }}>
            Escolha o enquadramento na faixa — sem ele o salário do reajuste fica zerado.
          </p>
        )}
      </>)}

      {/* Opções avançadas */}
      <div style={{ borderTop: `1px dashed ${C.border}`, paddingTop: 8 }}>
        <button type="button" onClick={onAlternarAvancado} style={{ background: "none", border: "none", padding: "4px 0", fontFamily: "inherit", fontSize: 12, fontWeight: 600, color: C.brand, cursor: "pointer" }}>
          {avancadoAberto ? "▾" : "▸"} Opções avançadas
        </button>
        {avancadoAberto && (
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.text, cursor: "pointer", marginTop: 6 }}
            title="Usar valor diferente do estabelecido na tabela salarial">
            <input type="checkbox" checked={!!reajuste.salario_excecao} style={{ cursor: "pointer" }}
              onChange={(e) => onChange({ salario_excecao: e.target.checked, ...(e.target.checked ? { salario: salarioTabela ?? atual.salario } : {}) })} />
            Desenquadramento — usar salário fora da tabela salarial
          </label>
        )}
      </div>
    </section>
  );
}

function botaoAdicionarReajusteStyle(C) {
  return {
    alignSelf: "flex-start", background: C.card, border: `1px dashed ${C.border}`, borderRadius: 8, padding: "10px 14px",
    fontFamily: "inherit", fontSize: 12.5, fontWeight: 700, color: C.brand, cursor: "pointer",
  };
}

function botaoRodapeStyle(C, tipo, desabilitado) {
  const base = { borderRadius: 8, padding: "10px 18px", fontFamily: "inherit", fontSize: 13, cursor: desabilitado ? "not-allowed" : "pointer", opacity: desabilitado ? 0.6 : 1 };
  if (tipo === "primario") return { ...base, background: C.brand, border: "none", color: "#fff", fontWeight: 700 };
  if (tipo === "secundario") return { ...base, background: C.card, border: `1px solid ${C.brand}`, color: C.brand, fontWeight: 700 };
  return { ...base, background: "transparent", border: `1px solid ${C.border}`, color: C.text, fontWeight: 600 };
}

// Salário da Nova contratação — mesmo fluxo do Reajuste: regime → agrupamento (do cargo no
// catálogo do RH; manual se o cargo não está lá) → enquadramento escolhido na faixa 80%–120%.
// Desenquadramento (salário fora da tabela) fica nas opções avançadas.
function SalarioNovaContratacao({ C, draft, itemCatalogo, tabelaSalarial, salarioTabela, salarioDaTabela, onChange }) {
  const [avancado, setAvancado] = useState(!!draft.salario_excecao);
  const agrupamento = itemCatalogo ? itemCatalogo.agrupamento : draft.agrupamento;
  const semAtual = { excecao: true };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12, alignItems: "end" }}>
        <CampoModal C={C} label="Regime">
          <CampoSelect C={C} valor={draft.regime_atual} onChange={(v) => onChange({ regime_atual: v })} opcoes={REGIMES_MAO_DE_OBRA} />
        </CampoModal>
        <CampoModal C={C} label={itemCatalogo ? "Agrupamento (do cargo)" : "Agrupamento"}>
          {itemCatalogo ? (
            <span title="Definido pelo cargo no catálogo do RH" style={{ ...celulaInputStyle(C), display: "block", fontWeight: 700, color: C.brand }}>
              {itemCatalogo.agrupamento}
            </span>
          ) : (
            <CampoSelectAgrupamento C={C} valor={draft.agrupamento} onChange={(v) => onChange({ agrupamento: v })} />
          )}
        </CampoModal>
        <div style={{ background: `${C.brand}12`, borderRadius: 8, padding: "6px 12px", textAlign: "right" }}>
          <span style={{ display: "block", fontSize: 11, color: C.muted, fontWeight: 600 }}>
            {draft.salario_excecao
              ? "Salário · fora da tabela"
              : `Salário · ${rotuloEnquadramento(draft.enquadramento)}${draft.regime_atual === "PJ" && tabelaSalarial ? ` · PJ +${formatarPercentualPj(tabelaSalarial.adicional_pj)}` : ""}`}
          </span>
          <span style={{ fontSize: 16, fontWeight: 800, color: C.text }}>
            {draft.salario_excecao
              ? formatCurrency(Number(draft.salario_bruto_atual || 0))
              : (salarioTabela != null ? formatCurrency(salarioTabela) : "—")}
          </span>
        </div>
      </div>

      {draft.salario_excecao ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: C.text }}>
          Salário fora da tabela:
          <span style={{ width: 160, border: `1px solid ${C.border}`, borderRadius: 8, background: C.bg }}>
            <CampoNumero C={C} valor={draft.salario_bruto_atual} onSalvar={(v) => onChange({ salario_bruto_atual: v })} />
          </span>
        </div>
      ) : (
        <FaixaEnquadramento C={C} agrupamento={agrupamento} regime={draft.regime_atual} selecionado={draft.enquadramento}
          atual={semAtual} salarioDaTabela={salarioDaTabela} variacao={() => null}
          onEscolher={(enq) => onChange({ enquadramento: enq })} />
      )}

      <div style={{ borderTop: `1px dashed ${C.border}`, paddingTop: 8 }}>
        <button type="button" onClick={() => setAvancado((v) => !v)} style={{ background: "none", border: "none", padding: "4px 0", fontFamily: "inherit", fontSize: 12, fontWeight: 600, color: C.brand, cursor: "pointer" }}>
          {avancado ? "▾" : "▸"} Opções avançadas
        </button>
        {avancado && (
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.text, cursor: "pointer", marginTop: 6 }}
            title="Usar valor diferente do estabelecido na tabela salarial">
            <input type="checkbox" checked={!!draft.salario_excecao} style={{ cursor: "pointer" }}
              onChange={(e) => onChange({ salario_excecao: e.target.checked, salario_bruto_atual: e.target.checked ? (salarioTabela || 0) : 0 })} />
            Desenquadramento — usar salário fora da tabela salarial
          </label>
        )}
      </div>
    </div>
  );
}

// Faixa do agrupamento (80%–120%): um cartão por enquadramento com o salário da tabela (já no
// regime do reajuste) e a variação sobre o salário atual — clicar escolhe o enquadramento.
function FaixaEnquadramento({ C, agrupamento, regime, selecionado, atual, salarioDaTabela, variacao, disabled, rotulo, onEscolher }) {
  if (agrupamento == null) {
    return (
      <p style={{ margin: 0, fontSize: 11, color: C.muted }}>
        {rotulo} Escolha o cargo (ou o agrupamento) para ver a faixa salarial de 80% a 120%.
      </p>
    );
  }
  return (
    <div style={{ opacity: disabled ? 0.5 : 1 }}>
      <p style={{ margin: "0 0 6px", fontSize: 11, fontWeight: 600, color: C.muted }}>
        {rotulo} Escolha o enquadramento na faixa do agrupamento {agrupamento}{regime === "PJ" ? " (PJ)" : ""}
      </p>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${ENQUADRAMENTOS.length}, minmax(0, 1fr))`, gap: 6 }}>
        {ENQUADRAMENTOS.map((enq) => {
          const valor = salarioDaTabela(agrupamento, enq, regime);
          const v = variacao(valor);
          const ativo = selecionado != null && Math.abs(Number(selecionado) - enq) < 0.001;
          const ehAtual = !atual.excecao && Number(atual.agrupamento) === Number(agrupamento) && Math.abs(Number(atual.enquadramento) - enq) < 0.001;
          return (
            <button key={enq} type="button" disabled={disabled} onClick={() => onEscolher(enq)}
              title={ehAtual ? "Enquadramento atual do colaborador" : undefined}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "7px 2px", borderRadius: 8,
                cursor: disabled ? "not-allowed" : "pointer", fontFamily: "inherit",
                border: `${ativo ? 2 : 1}px solid ${ativo ? C.brand : (ehAtual ? C.muted : C.border)}`,
                background: ativo ? C.brand : C.card, color: ativo ? "#fff" : C.text,
              }}>
              <span style={{ fontSize: 11, fontWeight: 800 }}>{Math.round(enq * 100)}%</span>
              <span style={{ fontSize: 10.5, fontWeight: 600 }}>{valor != null ? formatCurrency(valor) : "—"}</span>
              <span style={{ fontSize: 9.5, fontWeight: 700, color: ativo ? "#fff" : (v == null ? C.muted : v >= 0 ? C.success : C.danger) }}>
                {ehAtual ? "atual" : v == null ? "" : `${v >= 0 ? "+" : ""}${formatPercent(v)}`}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function miniBtnStyle(C, disabled) {
  return {
    background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6,
    padding: "4px 8px", fontSize: 10, fontWeight: 600, color: disabled ? C.muted : C.brand,
    cursor: disabled ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: disabled ? 0.5 : 1,
  };
}

// ── Células editáveis genéricas ──────────────────────────────────────────────

function CampoTexto({ C, valor, onSalvar, placeholder, larguraMin }) {
  const [texto, setTexto] = useState(valor);
  const [editando, setEditando] = useState(false);
  useEffect(() => { if (!editando) setTexto(valor); }, [valor, editando]);
  return (
    <input type="text" placeholder={placeholder} value={editando ? texto : (valor || "")}
      onFocus={() => setEditando(true)}
      onChange={(e) => setTexto(e.target.value)}
      onBlur={() => { setEditando(false); if (texto !== valor) onSalvar(texto); }}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
      style={larguraMin ? { ...celulaInputStyle(C), minWidth: larguraMin } : celulaInputStyle(C)} />
  );
}

function CampoNumero({ C, valor, onSalvar }) {
  const [texto, setTexto] = useState(String(valor ?? 0));
  const [editando, setEditando] = useState(false);
  useEffect(() => { if (!editando) setTexto(String(valor ?? 0)); }, [valor, editando]);
  function confirmar() {
    setEditando(false);
    const novo = parseFloat(String(texto).replace(",", ".")) || 0;
    if (novo !== Number(valor || 0)) onSalvar(novo);
  }
  return (
    <input type="text" inputMode="decimal" value={editando ? texto : formatCurrency(valor || 0)}
      onFocus={() => { setEditando(true); setTexto(String(valor ?? 0)); }}
      onChange={(e) => setTexto(e.target.value)} onBlur={confirmar}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
      style={{ ...celulaInputStyle(C), textAlign: "right" }} />
  );
}

function CampoSelect({ C, valor, onChange, opcoes, permiteVazio, disabled }) {
  return (
    <select value={valor ?? ""} onChange={(e) => onChange(e.target.value)} disabled={disabled}
      style={{ ...celulaInputStyle(C), ...(disabled ? { opacity: 0.6, cursor: "not-allowed" } : {}) }}>
      {permiteVazio && <option value="">—</option>}
      {opcoes.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function CampoSelectAgrupamento({ C, valor, onChange, permiteVazio }) {
  return (
    <select value={valor ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} style={celulaInputStyle(C)}>
      {permiteVazio && <option value="">—</option>}
      {AGRUPAMENTOS.map((a) => <option key={a} value={a}>{a}</option>)}
    </select>
  );
}

function CampoSelectEnquadramento({ C, valor, onChange, permiteVazio }) {
  return (
    <select value={valor ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} style={celulaInputStyle(C)}>
      {permiteVazio && <option value="">—</option>}
      {ENQUADRAMENTOS.map((e) => <option key={e} value={e}>{formatPercent(e * 100)}</option>)}
    </select>
  );
}

function CampoCheckbox({ C, valor, onChange }) {
  return (
    <div style={{ display: "flex", justifyContent: "center" }}>
      <input type="checkbox" checked={!!valor} onChange={(e) => onChange(e.target.checked)} style={{ cursor: "pointer" }} />
    </div>
  );
}

// Salário travado em tabela_salarial[agrupamento][enquadramento] — só libera edição
// manual quando a exceção está marcada (ver Ajuste 1 do documento de ajuste).
function CampoSalarioTabela({ C, valor, excecao, onSalvarValor, onToggleExcecao }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 84 }}>
      {excecao ? (
        <CampoNumero C={C} valor={valor} onSalvar={onSalvarValor} />
      ) : (
        <span title="Travado na tabela salarial (Agrupamento × Enquadramento)" style={{
          display: "block", textAlign: "right", padding: "5px 6px", fontSize: 12,
          color: C.muted, fontWeight: 600,
        }}>
          {formatCurrency(valor)}
        </span>
      )}
      <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 9, color: C.muted, cursor: "pointer", justifyContent: "flex-end" }}
        title="Usar valor diferente do estabelecido na tabela">
        <input type="checkbox" checked={!!excecao} onChange={(e) => onToggleExcecao(e.target.checked)} style={{ cursor: "pointer", width: 11, height: 11 }} />
        Desenquadramento
      </label>
    </div>
  );
}

// Filtro de coluna estilo Excel: botão de funil no cabeçalho abre uma lista de checkboxes com
// os valores únicos da coluna (calculados a partir de todos os colaboradores do departamento,
// não apenas dos já filtrados por outras colunas, para não "sumir" opções enquanto o usuário filtra).
// A lista é renderizada em portal (position: fixed) para não ser cortada pelo overflow da tabela
// — com poucas ou nenhuma linha filtrada, a tabela fica baixa e "comia" o dropdown.
const LARGURA_FILTRO = 240;
const ALTURA_MAX_FILTRO = 360;
const MARGEM_FILTRO = 8;

function posicaoFiltro(rect) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const left = Math.min(Math.max(rect.left, MARGEM_FILTRO), vw - LARGURA_FILTRO - MARGEM_FILTRO);
  const espacoAbaixo = vh - rect.bottom - MARGEM_FILTRO - 4;
  const espacoAcima = rect.top - MARGEM_FILTRO - 4;
  const abrirAcima = espacoAbaixo < 220 && espacoAcima > espacoAbaixo;
  const altura = Math.max(Math.min(ALTURA_MAX_FILTRO, abrirAcima ? espacoAcima : espacoAbaixo), 140);
  return abrirAcima ? { left, bottom: vh - rect.top + 4, altura } : { left, top: rect.bottom + 4, altura };
}

function FiltroColuna({ C, valores, selecionados, onChange }) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState("");
  const [posicao, setPosicao] = useState(null);
  const ref = useRef(null);
  const listaRef = useRef(null);

  useEffect(() => {
    if (!aberto) { setBusca(""); return undefined; }
    function aoClicarFora(e) {
      if (ref.current?.contains(e.target) || listaRef.current?.contains(e.target)) return;
      setAberto(false);
    }
    function aoTeclar(e) { if (e.key === "Escape") setAberto(false); }
    document.addEventListener("mousedown", aoClicarFora);
    document.addEventListener("keydown", aoTeclar);
    return () => {
      document.removeEventListener("mousedown", aoClicarFora);
      document.removeEventListener("keydown", aoTeclar);
    };
  }, [aberto]);

  useLayoutEffect(() => {
    if (!aberto) return undefined;
    function atualizar() { if (ref.current) setPosicao(posicaoFiltro(ref.current.getBoundingClientRect())); }
    function aoRolar(e) { if (!listaRef.current?.contains(e.target)) atualizar(); }
    atualizar();
    window.addEventListener("resize", atualizar);
    window.addEventListener("scroll", aoRolar, true);
    return () => {
      window.removeEventListener("resize", atualizar);
      window.removeEventListener("scroll", aoRolar, true);
    };
  }, [aberto]);

  if (valores.length === 0) return null;

  const filtroAtivo = !!selecionados;
  const marcados = selecionados || new Set(valores.map((v) => v.valor));

  function alternar(valor) {
    const novo = new Set(selecionados || valores.map((v) => v.valor));
    if (novo.has(valor)) novo.delete(valor); else novo.add(valor);
    onChange(novo.size === valores.length ? null : novo);
  }

  const valoresExibidos = busca ? valores.filter((v) => v.rotulo.toLowerCase().includes(busca.toLowerCase())) : valores;

  return (
    <span ref={ref} style={{ position: "relative", display: "inline-flex" }}>
      <button onClick={(e) => { e.stopPropagation(); setAberto((a) => !a); }} title="Filtrar coluna"
        style={{
          display: "flex", alignItems: "center", justifyContent: "center", width: 16, height: 16, padding: 0,
          border: "none", borderRadius: 3, cursor: "pointer",
          background: filtroAtivo ? "#7C3AED" : "rgba(255,255,255,0.18)", color: "#fff",
        }}>
        <Filter size={9} />
      </button>
      {aberto && posicao && createPortal(
        <div ref={listaRef} onClick={(e) => e.stopPropagation()} style={{
          position: "fixed", top: posicao.top, bottom: posicao.bottom, left: posicao.left, width: LARGURA_FILTRO,
          maxHeight: posicao.altura, zIndex: 1000, display: "flex", flexDirection: "column",
          background: C.card, border: `1px solid ${C.border}`, borderRadius: 10,
          boxShadow: "0 12px 32px rgba(0,0,0,0.3)", padding: 10, boxSizing: "border-box",
          textTransform: "none", textAlign: "left", fontWeight: 400, letterSpacing: "normal", color: C.text,
        }}>
          {valores.length > 8 && (
            <input autoFocus value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar…"
              style={{ width: "100%", boxSizing: "border-box", marginBottom: 8, padding: "6px 8px", fontSize: 12, borderRadius: 6, border: `1px solid ${C.border}`, background: C.bg, color: C.text, fontFamily: "inherit", flexShrink: 0 }} />
          )}
          <div style={{ display: "flex", gap: 8, marginBottom: 8, flexShrink: 0 }}>
            <button onClick={() => onChange(null)} style={{ ...miniBtnStyle(C, false), fontSize: 11 }}>Todos</button>
            <button onClick={() => onChange(new Set())} style={{ ...miniBtnStyle(C, false), fontSize: 11 }}>Nenhum</button>
          </div>
          <div style={{ overflowY: "auto", overscrollBehavior: "contain", minHeight: 0 }}>
            {valoresExibidos.map((v) => (
              <label key={String(v.valor)} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, padding: "4px 2px", cursor: "pointer" }}>
                <input type="checkbox" checked={marcados.has(v.valor)} onChange={() => alternar(v.valor)} style={{ cursor: "pointer", flexShrink: 0 }} />
                {v.rotulo}
              </label>
            ))}
            {valoresExibidos.length === 0 && <p style={{ fontSize: 12, color: C.muted, margin: "4px 0" }}>Nenhum valor encontrado.</p>}
          </div>
        </div>,
        document.body
      )}
    </span>
  );
}

// ── Estilos ───────────────────────────────────────────────────────────────────

const headerBtnStyle = {
  display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.12)",
  border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 12px",
  cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
};

function btnStyle(color, secondary = false) {
  return {
    display: "flex", alignItems: "center", gap: 6,
    background: secondary ? "transparent" : color,
    border: secondary ? `1px solid ${color}` : "none",
    color: secondary ? color : "#fff",
    borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700,
    fontFamily: "inherit", cursor: "pointer",
  };
}

function iconBtnStyle(C, danger = false) {
  return {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 22, height: 22, borderRadius: 6, border: `1px solid ${C.border}`,
    background: "transparent", color: danger ? C.danger : C.text, cursor: "pointer", flexShrink: 0,
  };
}

// `align` deve casar com o alinhamento real do valor exibido na célula (ver tdStyle/inputs
// abaixo) — cabeçalho e dado desalinhados é o motivo do texto do cabeçalho "flutuar" fora de
// cima da coluna quando a coluna é larga (centralizado no header × esquerda/direita no dado).
function thStyle(C, reajuste = false, align = "center") {
  return {
    padding: "10px 10px", fontSize: 10.5, fontWeight: 700, color: C.navyText,
    textTransform: "uppercase", letterSpacing: "0.03em", textAlign: align,
    background: reajuste ? "#1E2B4A" : C.navy, whiteSpace: "nowrap",
    position: "sticky", top: 0, zIndex: 2,
  };
}

function tdStyle(C, strong = false) {
  return {
    padding: "6px 9px", fontSize: 12.5, color: C.text, textAlign: "right",
    borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap",
    fontWeight: strong ? 700 : 400,
  };
}

function celulaInputStyle(C) {
  return {
    width: "100%", minWidth: 110, background: "transparent", border: "1px solid transparent",
    borderRadius: 4, padding: "6px 7px", fontSize: 12.5, color: C.text, fontFamily: "inherit", outline: "none",
  };
}
